import * as React from "react";
import * as TreeRepository from "../../Repository/TreeRepository";
import * as AuthService from "../../Service/AuthService";
import "mapbox-gl/dist/mapbox-gl.css";
import PQueue from "p-queue";
import MapStyleSwitcher from "./MapStyleSwitcher";
import DimensionToggle from "./Popup/DimensionToggle";
import TreeCounter from "../../Component/Page/TreeCounter";
import TreeTable from "./TreeTable";
import MapBoundingBox from "./MapBoundingBox";
import MapDataLoader from "./MapDataLoader";
import MapPopupErrorBoundary from "./Popup/MapPopupErrorBoundary";
import TreeMapErrorBoundary from "./TreeMapErrorBoundary";
import MoveTreesPopup from "./MoveTreesPopup";
import { Filter, MapPosition } from "./MapPage";
import {
  MapBoxSatelliteStreetsStyle,
  getMapStyles,
  mapStyles,
} from "./MapBoxConfig";
import { GeoJsonFile } from "tree-wallet-client";
import { GlobalContext } from "../../Contexts/GlobalContext";
import { MutableRefObject } from "react";
import { GeoJSONFeature, ResourceType } from "mapbox-gl";
import { createRoot, Root } from "react-dom/client";
import { request } from "../../Helper/Http";
import { headersConstants } from "../../Helper/Http";
import {
  getImpersonationData,
  isImpersonating,
} from "../../Service/RoleService";
import {
  getClusterCountLayer,
  getClustersLayer,
  getSelectedUnclusteredPointLayer,
  getUnclusteredPointLayer,
  getSelectionShadowLayer,
} from "./LayersConfig";
import { ArrowLeftOutlined } from "@ant-design/icons";
import _ from "lodash";

const queue = new PQueue({
  concurrency: 1,
});

const mapboxgl = require("mapbox-gl");

// `LngLat.distanceTo` is a haversine: it derives the distance from `acos` of a dot product, so it
// loses every bit of precision once the two points are closer than about 13 cm of ground - `cos Δλ`
// rounds to exactly 1, `acos(1)` is 0, and the two points are reported as being at the same place.
// The scale control unprojects two points 100 px apart, so from roughly zoom 26 on it got 0, and its
// rounding helper turns 0 into NaN (`Intl.NumberFormat` then prints "NaN m"). Under that distance
// the planar approximation below is exact to the millimetre, so it is used whenever the haversine
// comes back empty. Identical points still give 0.
const earthRadius = 6371008.8;
const haversineDistanceTo = mapboxgl.LngLat.prototype.distanceTo;

mapboxgl.LngLat.prototype.distanceTo = function distanceTo(other: any) {
  const haversine = haversineDistanceTo.call(this, other);

  if (haversine > 0 && Number.isFinite(haversine)) {
    return haversine;
  }

  const dLat = (other.lat - this.lat) * (Math.PI / 180) * earthRadius;
  const dLng = (other.lng - this.lng) * (Math.PI / 180) * earthRadius *
    Math.cos(((this.lat + other.lat) / 2) * (Math.PI / 180));

  return Math.hypot(dLat, dLng);
};

const baseApiUrl = process.env.BASE_API_URL;

const zoomLevels = {
  min: 1,
  med: 10,
  max: 25,
};

// The camera's own limit. mapbox-gl does not cap this itself (its `setMaxZoom` only rejects a value
// below `minZoom`); the basemap sources cap their own tile zooms and are magnified above them, which
// is accepted here - the point of zooming this far in is to pull tree icons standing a few metres
// apart away from each other, not to read the imagery. One pixel covers
// `78271.484 * cos(latitude) / 2^zoom` metres of ground:
//   z22 = 1.9 cm/px · z24 = 0.47 cm · z26 = 1.2 mm · z28 = 0.29 mm · z30 = 0.073 mm (equator).
// 30 is about as far as it stays sound: the tile space of a GeoJSON source snaps feature positions
// to a grid (`EXTENT`/`sourceMaxZoom` below, ~0.3 mm at the equator), which starts to show as icons
// snapping to a few pixels, and `2^zoom` scaling loses precision from roughly z40 on.
const mapMaxZoom = 30;

// A GeoJSON source only builds tiles up to its own `maxzoom` (18 by default) and the renderer
// *overscales* the last of them above that. Clustering is decided per tile zoom, so a
// `clusterMaxZoom` above the source's `maxzoom` is unreachable: the z18 tiles stay clustered and the
// badges were drawn at every camera zoom (that is why raising it to 24, then 20, changed nothing).
// Tiles are therefore generated up to the highest zoom the style spec allows (24), which also keeps
// `mapMaxZoom` useful: it is this tile level's precision that tree positions are drawn with when the
// map is magnified far beyond it.
const sourceMaxZoom = 24;

// Clustering (the count badges) stops at this zoom, deliberately well below `sourceMaxZoom` so the
// last tile zooms are unclustered (a `clusterMaxZoom` at or above the source's `maxzoom` clusters
// every tile, see above). From camera zoom 21 on, up to `mapMaxZoom`, the badges are gone and every
// tree is drawn as its own icon, overlapping ones included (see `getUnclusteredPointLayer()`).
// Exported because the badge layers are hidden from that zoom on as well: see `LayersConfig`.
export const clusterMaxZoom = 20;

// The smallest group that becomes a badge. Two tree icons that land close enough to hide each other
// are merged into a badge instead of leaving one of them undrawn. Supercluster's own minimum (and
// default) is 2, so this is the smallest group that can be turned into a badge.
const clusterMinPoints = 2;

// The clustering radius, in pixels of the 512 px tile mapbox measures these in. It is what the three
// sources in this file are created with (`clusterRadius: 30`), and what a drilled-down frame is grown
// by: a badge's trees can sit one radius away from the badge's own position (see
// `growBoundsByClusteringRadius`).
const clusterRadiusPx = 30;

// Asking a badge for its trees costs one feature per tree across the worker boundary (Uganda: 71,558 =
// 17 MB), so it is only done up to this many of them. A heavier badge has its drawn pieces collected
// from the cluster tree instead (see `frameClusterFamily`).
const maxClusterLeaves = 2000;

// The most pieces that tree walk collects before giving up and framing a radius-wide margin instead.
const maxClusterWalk = 600;

// Margin left on each side of a drilled-down badge: 15% of the viewport per side, i.e. the badges and tree
// icons it is made of span 70% of the map. (The country view keeps a tenth per side, i.e. 80%.)
const clusterFramePadding = 0.15;

// The scale bar: the widest bar we draw, and the narrowest, in pixels. The label is picked from the
// 1-2-5 ladder of metres below `scaleBarMaxWidth`, so the bar stays between these two widths at any
// zoom.
const scaleBarMaxWidth = 100;
const scaleBarMinWidth = 24;

/**
 * A ground distance in metres, in the unit that keeps it readable. Mapbox's own scale control only
 * knows kilometres and metres, which is why it printed fractions of a metre once the map was zoomed
 * far enough in.
 */
function formatScaleDistance(meters: number): string {
  const round = (value: number) => Number(value.toFixed(2));

  if (meters >= 1000) {
    return `${round(meters / 1000)} km`;
  }

  if (meters >= 1) {
    return `${round(meters)} m`;
  }

  if (meters >= 0.01) {
    return `${round(meters * 100)} cm`;
  }

  return `${round(meters * 1000)} mm`;
}

export interface RemovedTrees {
  [key: string]: number[];
}

// Dynamic zoom configuration interface
interface DynamicZoomConfig {
  maxZoom: number;
  padding: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  duration: number;
}

interface CountryGeometryAnalysis {
  area: number;
  aspectRatio: number;
  boundingBoxArea: number;
  complexity: number;
  isElongated: boolean;
  isFragmented: boolean;
}

type TreeMapsState = {
  loading: boolean;
  mapStyle: string;
  removedTrees: any;
  featuresToMove?: any[];
  features?: any[];
  countryTreeCounts?: any[];
  selectedCountryId?: string | null;
  selectedTreeId?: number | string | null;
  countryFeaturesByIso: { [key: string]: any[] };
  treesVisible: boolean;
  originalFeatures?: any[];
  is3D: boolean;
  previousSelectedCountryId?: string | null;
  showErrorModal?: boolean;
};

type TreeMapsProps = {
  initialPosition: MapPosition;
  language: string;
  template: any;
  geoJsonFiles: GeoJsonFile[];
  public?: boolean;
  tableRef?: React.Ref<TreeTable>;
  countrySpecificData?: any;
  setCountrySpecificData?: (data: any) => void;
  // True while the parent is fetching `countrySpecificData` for the selected country. It lets the
  // map stop waiting for features as soon as the request has settled instead of relying on a timeout
  countryDataLoading?: boolean;
  highlightedCountries?: any[];
  selectedCountryId?: string;
  onCountrySelect?: (iso: string) => void;
  onCountryDeselect?: () => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
};

export const layers = {
  selectedUnclusteredPoint: "selected-unclustered-point",
  selectedUnclusteredPointShadow: "selected-unclustered-point-shadow",
  selectedClusters: "selected-clusters",
  unclusteredPoint: "unclustered-point",
  countriesCountLabel: "countries-count-label",
  clusterCount: "cluster-count",
  clusters: "clusters",
  countries: "countries",
  countriesHighlight: "countries-highlight",
  countryLabels: "country-labels",
};

export const images = [
  { url: "/dist/icons/tree_icon_shade.svg", id: "tree-shade" },
  { url: "/dist/icons/tree_icon_fruit.svg", id: "tree-fruit" },
];

const layerIds: any[] = [];

export const getLayerId = (layerId: string, sourceId: string) => {
  return `${layerId}-${sourceId}`;
};

export default class TreeMaps extends React.Component<
  TreeMapsProps,
  TreeMapsState
> {
  static contextType = GlobalContext;
  private countryBoundsCache: {
    [key: string]: [number, number, number, number];
  } = {};
  private countryCenterCache: {
    [key: string]: [number, number];
  } = {};
  declare context: React.ContextType<typeof GlobalContext>;
  private readonly accessToken: string;
  private map: any;
  private readonly clusteredLayers: any[] = [];
  private currentFilter: Filter;
  private previousFilteredTreeId: number | string | null = null;
  private mapBoundingBox: MapBoundingBox;
  private selectedCountryId: string | null = null;
  private highlightedCountries: string[] = [];
  private handlerRefs: Map<string, any> = new Map();
  private isPopupCurrentlyOpen: boolean = false;
  private currentPopupTreeId: any = null;
  private currentSelectedTreeId: number | string | null = null;
  // Popups are mounted with `createRoot` on a detached node, so closing them by removing the node
  // from the DOM left their React tree - and whatever it registered - alive. Tracking the roots is
  // what lets every close path unmount them properly.
  private openPopups: Map<HTMLElement, Root> = new Map();
  // Bumped whenever a pending popup open must be abandoned (a close, a map reset), so an open
  // request that is waiting for the map frames which display the selection shadow can drop itself.
  private popupOpenToken: number = 0;
  // Where the tree of the currently open popup is. Kept as coordinates rather than as a screen
  // point: the map can still be moving (a country zoom) between the click and the moment the
  // popup closes, and `getPopupMarkerScreenPoint()` projects them at that moment.
  private popupMarkerCoordinates: [number, number] | null = null;
  // Trees whose deletion the server has accepted and whose icon is on its way out: it stays drawn until the
  // popup that asked for it is over, and a click on it must do nothing - it would open a window for a tree
  // that is gone.
  private treesBeingDeleted: { [treeId: string]: boolean } = {};
  // The deletion waiting for the popup that asked for it to be over: the icon only goes once that window has
  // closed (see `resetPopupState`), and it cannot be clicked in the meantime.
  private deletionAwaitingPopupClose: { treeId: any, increment: number } | null = null;
  private countryTransitionTimeout: any = null;
  // Cancels the return-to-world flight of a Back that is still in progress (see `handleBackButtonClick`):
  // a country opened in the meantime takes the camera over
  private cancelWorldViewReset: (() => void) | null = null;
  // The country opening currently rendering (see `selectCountryDirectly` and the "new country data
  // arrived" branch of `componentDidUpdate`, which must leave that render to it)
  private countryViewOpening: string | null = null;
  private clickProcessing: boolean = false;
  // A click on a country badge is delivered to both the circle layer and its label, so the
  // two callbacks land in the same tick - where `this.state.loading` is still false for the
  // second one, because setState is asynchronous. This synchronous guard coalesces them,
  // so one click starts one selection and therefore one cluster render.
  private countryBadgeClickProcessing: boolean = false;
  private isTransitioningToNewPopup: boolean = false;
  private scaleContainerRef: React.RefObject<HTMLDivElement>;
  // The scale label inside that container. It is written directly on map moves rather than through
  // state: re-rendering this component on every frame of a drag is not free.
  private scaleLabelRef: React.RefObject<HTMLDivElement>;
  private orientationHandler: ((event: DeviceOrientationEvent) => void) | null = null;
  private orientationEventName: "deviceorientation" | "deviceorientationabsolute" | null = null;

  constructor(props: TreeMapsProps) {
    super(props);
    this.scaleContainerRef = React.createRef();
    this.scaleLabelRef = React.createRef();
    this.removeTree = this.removeTree.bind(this);
    this.openPopup = this.openPopup.bind(this);
    this.changePosition = this.changePosition.bind(this);
    this.loadImages = this.loadImages.bind(this);
    this.updateFeaturesStates = this.updateFeaturesStates.bind(this);
    this.processSource = this.processSource.bind(this);
    this.getFeaturesStates = this.getFeaturesStates.bind(this);
    this.updateScale = this.updateScale.bind(this);

    this.accessToken = process.env.MAPBOX_TOKEN || "";
    this.map = undefined;
    this.isPopupCurrentlyOpen = false;
    this.currentPopupTreeId = null;
    this.currentSelectedTreeId = null;
    this.clickProcessing = false;
    this.state = {
      featuresToMove: undefined,
      features: [],
      originalFeatures: [],
      mapStyle: props.public
        ? getMapStyles().topographic || mapStyles.topographic
        : mapStyles.topographic,
      loading: true,
      removedTrees: {},
      selectedCountryId: null,
      countryFeaturesByIso: {},
      treesVisible: !props.public,
      is3D: false,
      previousSelectedCountryId: null,
      showErrorModal: false,
    };
  }

  // Store handler references with unique keys
  storeHandlerRef(key: string, handler: any) {
    this.handlerRefs.set(key, handler);
  }

  getHandlerRef(key: string) {
    return this.handlerRefs.get(key);
  }

  removeHandlerRef(key: string) {
    this.handlerRefs.delete(key);
  }

  componentDidMount() {
    const { geoJsonFiles, initialPosition } = this.props;
    if (this.map) {
      return;
    }

    if (!mapboxgl.supported || !mapboxgl.supported({ failIfMajorPerformanceCaveat: false })) {
      console.warn("WebGL is not supported or context was lost in this browser environment.");
      return;
    }

    try {
      mapboxgl.accessToken = this.accessToken;
      this.map = new mapboxgl.Map({
        container: "map",
        center: initialPosition.coordinates,
        style: MapBoxSatelliteStreetsStyle + "?optimize=true",
        zoom: initialPosition.zoomLevel || zoomLevels.med,
        minZoom: 1,  // Allow zooming out to see the whole world
        maxZoom: mapMaxZoom, // Camera zoom limit, see the constant at the top of the file
        pitch: 0,    // Add pitch to see 3D terrain
        bearing: 0,
        failIfMajorPerformanceCaveat: false,
        // Performance optimizations:
        antialias: false,                    // Disable antialiasing for better performance
        preserveDrawingBuffer: false,        // Don't preserve drawing buffer
        refreshExpiredTiles: false,          // Don't auto-refresh expired tiles
        maxTileCacheSize: 50,
        // Symbols and tiles switch instantly instead of cross-fading. The default is 300 ms, and
        // mapbox *stretches* that fade while the camera is zooming (`Placement#getFadeStartTime`
        // adds a zoom adjustment), which is what made the badge counters vanish for about a second
        // while zooming between tile zoom levels - long after their circles, drawn from the same
        // tile, were in place. 0 is the mode mapbox itself uses for the first load
        // (`fadeDuration: this._isInitialLoad ? 0 : this._fadeDuration`). Raster layers keep their
        // own `raster-fade-duration`, so the satellite basemap still cross-fades.
        fadeDuration: 0,
        transformRequest: (url: string, resourceType: ResourceType) => {
          if (
            baseApiUrl &&
            resourceType === "Source" &&
            url.startsWith(baseApiUrl) &&
            !/\/public/.exec(url)
          ) {
            let headers = {
              [headersConstants.authorization]:
                "Bearer " + AuthService.getToken(),
            };
            const impersonationData = getImpersonationData();
            if (isImpersonating() && impersonationData?.user) {
              headers = {
                ...headers,
                ...{ [headersConstants.impersonation]: impersonationData.user },
              };
            }
            return { url, headers };
          }
          return null;
        },
      });
    } catch (err) {
      console.error("Failed to initialize Mapbox GL map:", err);
      return;
    }

    this.map.on("load", async () => {
      console.log("Map loaded, starting to add sources...");

      let highlightPromise: Promise<void> | null = null;

      // Add countries layer if public mode
      if (this.props.public) {
        await this.addCountriesLayer();
        // The public map has no `geoJsonFiles`, so `processSource()` - which used to be the only
        // caller of `loadImages()` here - never runs. The tree markers need these images
        // registered because their `icon-image` references them, including the SVG ones
        // (`tree-fruit` / `tree-shade`, used for the unclustered tree markers).
        await this.loadImages(images);
        // Wait a bit for the countries layer to be fully ready before highlighting
        highlightPromise = new Promise<void>(resolve => {
          setTimeout(async () => {
            await this.autoHighlightCountries();
            resolve();
          }, 500);
        });
      }

      await queue.onIdle().then(() => {
        this.setState({ loading: true });
        this.props.onLoadingStateChange?.(true);
      });

      // Load tree sources
      await this.loadSources(geoJsonFiles);

      // Categorize features by country in public mode
      if (this.props.public) {
        this.categorizeFeaturesByCountry();
      }

      // Initialize other components
      if (!this.props.public) {
        this.mapBoundingBox = new MapBoundingBox({
          map: this.map,
          clusteredLayers: this.clusteredLayers,
          geoJsonFiles: geoJsonFiles,
          onFinish: (ids: number[]) => {
            this.setState({ featuresToMove: ids });
          },
        });
        this.mapBoundingBox.setData(this.state.features);
        this.mapBoundingBox.set();
      }

      // Add controls
      const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const geolocateBtn = new mapboxgl.GeolocateControl({
        fitBoundsOptions: {
          maxZoom: 18,
        },
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
        showAccuracyCircle: true,
        showUserHeading: isIOSDevice,
      });

      this.map.addControl(geolocateBtn);
      this.map.addControl(new mapboxgl.NavigationControl(), "top-right");

      const updateHeadingMarker = (event: DeviceOrientationEvent) => {
        const compassEvent = event as DeviceOrientationEvent & {
          webkitCompassHeading?: number;
        };
        const hasWebkitHeading = typeof compassEvent.webkitCompassHeading === "number";
        const heading = hasWebkitHeading
          ? compassEvent.webkitCompassHeading
          : event.alpha === null
            ? null
            : (360 - event.alpha) % 360;

        if (typeof heading !== "number" || !Number.isFinite(heading)) return;

        const screenAngle = window.screen.orientation?.angle ?? 0;
        const adjustedHeading = (heading + screenAngle + 360) % 360;
        const userLocationMarker = (geolocateBtn as any)._userLocationDotMarker;

        if (userLocationMarker?.setRotation) {
          userLocationMarker.setRotation(adjustedHeading);
          userLocationMarker.addClassName?.("mapboxgl-user-location-show-heading");
        }
      };

      if (isIOSDevice) {
        this.orientationHandler = updateHeadingMarker;
        this.orientationEventName = "deviceorientation";
        const control = geolocateBtn as any;
        const orientationApi = DeviceOrientationEvent as typeof DeviceOrientationEvent & {
          requestPermission?: () => Promise<"granted" | "denied">;
        };

        control._addDeviceOrientationListener = () => {
          const registerListener = () => {
            if (this.orientationEventName && this.orientationHandler) {
              window.removeEventListener(
                this.orientationEventName,
                this.orientationHandler
              );
              window.addEventListener(
                this.orientationEventName,
                this.orientationHandler
              );
            }
          };

          if (typeof orientationApi.requestPermission === "function") {
            orientationApi.requestPermission().then((permission) => {
              if (permission === "granted") registerListener();
            }).catch((error) => {
              console.warn("Device orientation permission was not granted:", error);
            });
          } else {
            registerListener();
          }
        };
      }

      // The scale bar is drawn by `updateScale()`: mapbox's own control measures its bar with a
      // haversine that reports 0 below ~13 cm of ground (see the note on the patched prototype at
      // the top of the file) and can only format kilometres and metres on top of that.
      this.map.on("move", this.updateScale);
      this.updateScale();

      await geolocateBtn.trigger();

      if (!isIOSDevice) {
        this.orientationHandler = updateHeadingMarker;
        this.orientationEventName = "ondeviceorientationabsolute" in window
          ? "deviceorientationabsolute"
          : "deviceorientation";
        window.addEventListener(this.orientationEventName, this.orientationHandler);
      }

      if (highlightPromise) {
        await highlightPromise;
      }

      setTimeout(() => {
        // Safety: only turn off loading if we aren't in the middle of a country selection
        if (!this.selectedCountryId) {
          this.setState({ loading: false });
          this.props.onLoadingStateChange?.(false);
        }
      }, 100);
    });
  }

  /**
   * Draws the scale bar: a round ground distance (1-2-5 x 10^n metres, shown in km/m/cm/mm) on a bar
   * as wide as that distance measures on screen. It runs on every map move, so it follows the zoom,
   * and writes to the label element directly rather than through state - re-rendering this component
   * on every frame of a drag is not free.
   */
  private updateScale() {
    const label = this.scaleLabelRef.current;

    if (!this.map || !label) {
      return;
    }

    // Mapbox tiles are 512 px wide, so one pixel covers this much ground at this latitude and zoom.
    const latitude = this.map.getCenter().lat;
    const metersPerPixel = this.metresPerPixel(latitude, this.map.getZoom());

    const targetMeters = metersPerPixel * scaleBarMaxWidth;
    const decade = Math.pow(10, Math.floor(Math.log10(targetMeters)));
    const meters = [5, 2, 1]
      .map((factor) => factor * decade)
      .find((value) => value <= targetMeters) ?? decade;

    label.style.width = `${Math.max(scaleBarMinWidth, Math.round(meters / metersPerPixel))}px`;
    label.textContent = formatScaleDistance(meters);
  }

  componentDidUpdate(prevProps: TreeMapsProps): void {
    if (!this.props.public) return;

    // Check if highlightedCountries just became available
    const prevHighlighted = prevProps?.highlightedCountries;
    const currHighlighted = this.props.highlightedCountries;

    const prevHadHighlighted = !!(prevHighlighted && Array.isArray(prevHighlighted) && prevHighlighted.length > 0);
    const currHasHighlighted = !!(currHighlighted && Array.isArray(currHighlighted) && currHighlighted.length > 0);

    // When highlighted countries data becomes available, auto-highlight them
    if (!prevHadHighlighted && currHasHighlighted && this.map) {
      console.log("componentDidUpdate: highlightedCountries became available, calling autoHighlightCountries");

      this.setState({ loading: true });
      this.props.onLoadingStateChange?.(true);

      // Add a small delay to ensure the map is fully ready
      setTimeout(async () => {
        await this.autoHighlightCountries();

        // Safety: only turn off loading if we aren't in the middle of a specific country selection
        if (!this.selectedCountryId) {
          this.setState({ loading: false });
          this.props.onLoadingStateChange?.(false);
        }
      }, 200);
    }

    const prevData = prevProps?.countrySpecificData as any;
    const currData = this.props.countrySpecificData as any;

    if (prevProps.selectedCountryId !== this.props.selectedCountryId) {
      // Handle external selection changes if needed
    }

    const prevHasFeatures = !!(prevData && prevData.features && Array.isArray(prevData.features) && prevData.features.length > 0);
    const currHasFeatures = !!(currData && currData.features && Array.isArray(currData.features) && currData.features.length > 0);

    // When new country-specific data arrives, render it for the country that is open
    if (!prevHasFeatures && currHasFeatures) {
      // The generic `country-trees` source used to be created from this payload as well, but on
      // the public map its layers are always hidden (the dedicated `country-trees-<iso>` source
      // is what renders), so it only duplicated the payload in a second clustered source - a
      // second 16.7 MB of work for Uganda. `showTreesForCountry()` feeds the dedicated source
      // from the very same props.

      // If the view was left (or another country was opened) while this payload was on its way,
      // nothing rendered it: the wait for the data ends with the selection change. Rendering it
      // here is what guarantees the country view is never left empty.
      const openIso = this.selectedCountryId;
      if (openIso) {
        // The flow that is opening a country owns this render (see `countryViewOpening`): it fits the camera on
        // the country first and renders the clusters right after, so they can never be drawn at with default world zoom the ma
        if (this.countryViewOpening === openIso) {
          return;
        }

        this.showTreesForCountry(openIso).catch((error) =>
          console.warn(`Error while rendering the data of the open country (${openIso}):`, error)
        );
      }
      return;
    }

    // When country-specific data is cleared, restore the initial state. The country counts are NOT
    // rebuilt here any more: the two flows that clear the data (`handleBackButtonClick` and the
    // "close the country I already have open" path) call `refreshWorldViewCounts()` themselves, so
    // doing it here as well ran the same ~600ms chain twice on every Back.
    if (prevHasFeatures && !currHasFeatures) {
      const sourceId = "country-trees";

      // Remove ALL layers for the country source in the EXACT correct order
      // shadow -> selectedPoint -> unclusteredPoint -> clusterCount -> clusters
      [
        layers.selectedUnclusteredPointShadow,
        layers.selectedUnclusteredPoint,
        layers.unclusteredPoint,
        layers.clusterCount,
        layers.clusters
      ].forEach(
        (layerType) => {
          const layerId = getLayerId(layerType, sourceId);
          if (this.map.getLayer(layerId)) {
            this.map.removeLayer(layerId);
          }
        }
      );
      if (this.map.getSource(sourceId)) {
        this.cleanupEventHandlers(sourceId);
        this.map.removeSource(sourceId);
      }

      // Restore default tree layers
      this.showAllTrees();

      this.setState({
        selectedCountryId: null,
        treesVisible: true,
      });
    }
  }

  // Dynamic zoom calculation based on country geometry analysis
  calculateDynamicZoomConfig(iso: string, isMobile: boolean = false): DynamicZoomConfig {
    const bounds = this.getCountryBounds(iso);
    if (!bounds) {
      return this.getDefaultZoomConfig(isMobile);
    }
    const geometryAnalysis = this.analyzeCountryGeometry(iso, bounds);
    const baseZoom = this.calculateOptimalZoom(geometryAnalysis, isMobile);
    const padding = this.calculateAdaptivePadding(geometryAnalysis, isMobile);
    const duration = this.calculateAnimationDuration(baseZoom);

    return {
      maxZoom: baseZoom,
      padding,
      duration
    };
  }

  calculateAdaptivePadding(analysis: CountryGeometryAnalysis, isMobile: boolean): {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } {
    const basePadding = {
      top: isMobile ? 120 : 100,
      bottom: isMobile ? 80 : 70,
      left: isMobile ? 50 : 60,
      right: isMobile ? 50 : 60
    };

    if (analysis.isElongated) {
      if (analysis.aspectRatio > 2.5) {
        basePadding.top += 30;
        basePadding.bottom += 20;
      } else {
        basePadding.left += 30;
        basePadding.right += 30;
      }
    }

    if (analysis.isFragmented) {
      basePadding.top += 20;
      basePadding.bottom += 15;
      basePadding.left += 20;
      basePadding.right += 20;
    }

    if (analysis.boundingBoxArea > 100) {
      basePadding.top += 40;
      basePadding.bottom += 30;
      basePadding.left += 30;
      basePadding.right += 30;
    }

    return basePadding;
  }

  calculateAnimationDuration(targetZoom: number): number {
    const currentZoom = this.map.getZoom();
    const zoomDifference = Math.abs(targetZoom - currentZoom);
    const baseDuration = 1000;
    const additionalDuration = zoomDifference * 150;
    return Math.min(2000, Math.max(800, baseDuration + additionalDuration));
  }

  calculateGeometryArea(geometry: any): number {
    if (geometry.type === "Polygon") {
      return this.calculatePolygonArea(geometry.coordinates[0]);
    } else if (geometry.type === "MultiPolygon") {
      return geometry.coordinates.reduce((total: number, polygon: any) => {
        return total + this.calculatePolygonArea(polygon[0]);
      }, 0);
    }
    return 0;
  }

  calculateGeometryComplexity(geometry: any): number {
    let totalVertices = 0;

    const countVertices = (coords: any) => {
      if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
        coords.forEach(countVertices);
      } else if (Array.isArray(coords[0])) {
        totalVertices += coords.length;
      }
    };

    countVertices(geometry.coordinates);

    if (totalVertices > 1000) return 3;
    if (totalVertices > 500) return 2.5;
    if (totalVertices > 200) return 2;
    if (totalVertices > 100) return 1.5;
    return 1;
  }

  getDefaultZoomConfig(isMobile: boolean): DynamicZoomConfig {
    return {
      maxZoom: isMobile ? 5 : 6,
      padding: {
        top: isMobile ? 120 : 100,
        bottom: isMobile ? 80 : 70,
        left: isMobile ? 50 : 60,
        right: isMobile ? 50 : 60
      },
      duration: 1200
    };
  }

  async selectCountryDirectly(iso: string) {
    if (!this.map || !this.props.public) return;

    // Start loading immediately to cover the entire transition and render lifecycle
    this.setState({ loading: true });
    this.props.onLoadingStateChange?.(true);

    if (this.countryTransitionTimeout) {
      clearTimeout(this.countryTransitionTimeout);
      this.countryTransitionTimeout = null;
    }

    if (this.map && this.map.stop) this.map.stop();

    // A return flight from a Back that is still in progress is finished with the camera: its own reset lands a
    // `jumpTo` on the world view, and that landing - its 2500 ms fallback in particular - could fire while this
    // country was already flying, cancelling the new country zoom leaving its clusters drawn at default zoom
    this.cancelWorldViewReset?.();

    const highlightedCountry = this.props.highlightedCountries!.find((c) => c.country_id === iso);
    if (!highlightedCountry) {
      this.setState({ loading: false });
      this.props.onLoadingStateChange?.(false);
      return;
    }

    if (this.state.selectedCountryId === iso) {
      await this.resetToInitialCondition();
      if (this.props.onCountryDeselect) {
        this.props.onCountryDeselect();
      }
      // The world view is back: rebuild its counts and wait for them, exactly like the Back button
      // does, so the overlay goes with them on screen.
      await this.refreshWorldViewCounts();
      this.setState({ loading: false });
      this.props.onLoadingStateChange?.(false);
      return;
    }

    this.setState({
      selectedCountryId: iso,
    });

    this.selectedCountryId = iso;

    if (this.props.onCountrySelect) {
      const twoDigitCode = this.getTwoDigitIsoCode(iso);
      if (twoDigitCode) {
        this.props.onCountrySelect(twoDigitCode);
      } else {
        this.props.onCountrySelect(iso);
      }
    }

    if (this.map.getLayer(layers.countriesHighlight)) {
      this.map.setFilter(layers.countriesHighlight, ["==", "iso_3166_1_alpha_3", ""]);
    }

    this.map.setFilter(layers.countriesHighlight, [
      "==",
      "iso_3166_1_alpha_3",
      iso,
    ]);

    this.forceHideAllCountryLayers();

    // Here onwards, this flow owns the render of this country's clusters
    this.countryViewOpening = iso;

    // The camera has to land on the country before its clusters can be shown: if it stays
    // elsewhere the clusters are rendered off screen. The country boundary tiles may not be
    // loaded yet (slow network, or right after a Back that evicted them), so this waits for them
    // instead of guessing with a fixed delay. It gives up as soon as another country is selected,
    // so Back always escapes it.
    let bounds = this.getCountryMainLandmassBounds(iso);

    while (!bounds && this.selectedCountryId === iso) {
      console.log(`Bounds for ${iso} not available yet, waiting for the country tiles...`);
      await new Promise(resolve => setTimeout(resolve, 200));
      bounds = this.getCountryBounds(iso);
    }

    if (bounds) {
      const mapContainer = this.map.getContainer();
      const containerWidth = mapContainer.offsetWidth;
      const isMobile = containerWidth < 768;
      const dynamicConfig = this.calculateDynamicZoomConfig(iso, isMobile);

      // Apply 2D or 3D view to selected country
      const is3D = this.state.is3D;
      if (!is3D && this.map.isStyleLoaded()) {
        this.map.setTerrain(null);
        this.map.setFog(null);
      }

      // The country data is prepared and its clusters rendered while the camera is still flying to the country,
      // so the "Loading data..." overlay can be removed as soon as the clusters are on screen
      this.map.fitBounds(bounds, {
        // A tenth of the viewport on each side, so the country's boundary spans 80% of the map on the
        // side that constrains the fit (the country's own aspect ratio decides which one, and the
        // other side takes less). The heuristic padding and zoom cap this call used before are gone:
        // the box is the country itself now, so the fit *is* the answer, and a small country is meant
        // to fill the map as well. Only the flight duration is still taken from the dynamic config,
        // as it scales with the distance to travel.
        padding: {
          top: 0.1 * mapContainer.offsetHeight,
          bottom: 0.1 * mapContainer.offsetHeight,
          left: 0.1 * containerWidth,
          right: 0.1 * containerWidth,
        },
        maxZoom: mapMaxZoom,
        duration: dynamicConfig.duration,
        pitch: is3D ? this.map.getPitch() : 0,
        bearing: is3D ? this.map.getBearing() : 0,
        essential: true
      });
    }

    // A small gap so the camera animation starts before the (potentially heavy) source of the
    // country is built in the same tick.
    this.countryTransitionTimeout = setTimeout(async () => {
      await this.showTreesForCountry(iso);

      // The clusters of this country are rendered: a payload that lands later is handled by
      // `componentDidUpdate` again (see `countryViewOpening`)
      this.countryViewOpening = null;

      // Safety check: if the selected country changed while we were loading,
      // don't turn off the loading state here. The new selection will handle it.
      if (this.selectedCountryId !== iso) {
        return;
      }

      this.forceHideAllCountryLayers();
      this.countryTransitionTimeout = null;
      this.setState({ loading: false });
      this.props.onLoadingStateChange?.(false);
    }, 100);
  }

  calculateOptimalZoom(analysis: CountryGeometryAnalysis, isMobile: boolean): number {
    let baseZoom: number;

    if (analysis.boundingBoxArea > 200) {
      baseZoom = 3.5;
    } else if (analysis.boundingBoxArea > 100) {
      baseZoom = 4.2;
    } else if (analysis.boundingBoxArea > 50) {
      baseZoom = 4.8;
    } else if (analysis.boundingBoxArea > 20) {
      baseZoom = 5.5;
    } else if (analysis.boundingBoxArea > 5) {
      baseZoom = 6.2;
    } else if (analysis.boundingBoxArea > 1) {
      baseZoom = 7.0;
    } else {
      baseZoom = 8.0;
    }

    if (analysis.isElongated) {
      baseZoom -= 0.5;
    }
    if (analysis.isFragmented) {
      baseZoom -= 0.3;
    }
    if (analysis.complexity > 2) {
      baseZoom -= 0.2;
    }
    if (isMobile) {
      baseZoom = Math.max(3, baseZoom - 0.8);
    }
    // Allow zooming in much closer for detailed viewing
    // The minimum zoom level is 3, but no upper limit is set here
    // The actual max zoom is controlled by the map's maxZoom property (25)
    return Math.max(3, baseZoom);
  }

  analyzeCountryGeometry(iso: string, bounds: [number, number, number, number]): CountryGeometryAnalysis {
    const [west, south, east, north] = bounds;
    const width = east - west;
    const height = north - south;
    const boundingBoxArea = width * height;

    const features = this.map.querySourceFeatures("countries", {
      sourceLayer: "country_boundaries",
      filter: ["==", "iso_3166_1_alpha_3", iso],
    });

    let actualArea = 0;
    let complexity = 1;
    let isFragmented = false;

    if (features && features.length > 0) {
      const geometry = features[0].geometry;
      actualArea = this.calculateGeometryArea(geometry);
      complexity = this.calculateGeometryComplexity(geometry);
      isFragmented = geometry.type === "MultiPolygon";
    }

    const aspectRatio = width / height;
    const isElongated = aspectRatio > 2.5 || aspectRatio < 0.4;

    return {
      area: actualArea,
      aspectRatio,
      boundingBoxArea,
      complexity,
      isElongated,
      isFragmented
    };
  }

  getTwoDigitIsoCode(threeDigitIso: string): string | null {
    try {
      const features = this.map.querySourceFeatures("countries", {
        sourceLayer: "country_boundaries",
        filter: ["==", "iso_3166_1_alpha_3", threeDigitIso],
      });

      if (features && features.length > 0) {
        const feature = features[0];
        const properties = feature.properties;

        const twoDigitCode = properties.iso_3166_1 ||
          properties.iso_a2 ||
          properties.iso2 ||
          properties.ISO_A2 ||
          properties.alpha_2;

        if (twoDigitCode) {
          return twoDigitCode;
        }
      }
      return null;
    } catch (error) {
      console.error(`Error getting 2-digit ISO code for ${threeDigitIso}:`, error);
      return null;
    }
  }

  aggregatedFeaturesData(features: any) {
    // Return features directly without aggregation to preserve all individual tree data points
    // The aggregation was causing data loss where multiple trees with the same ID were merged
    return features;
  }

  calculateCountryBounds(geometry: any): [number, number, number, number] {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    const processCoordinates = (coords: any) => {
      if (Array.isArray(coords[0])) {
        coords.forEach(processCoordinates);
      } else {
        const [x, y] = coords;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    };

    processCoordinates(geometry.coordinates);
    return [minX, minY, maxX, maxY];
  }

  getCountryBounds(iso: string): [number, number, number, number] | null {
    // Validate the iso parameter
    if (!iso || iso === null || iso === undefined || iso === "") {
      console.warn("getCountryBounds: Invalid ISO code provided");
      return null;
    }

    if (this.countryBoundsCache[iso]) {
      return this.countryBoundsCache[iso];
    }

    const features = this.map.querySourceFeatures("countries", {
      sourceLayer: "country_boundaries",
      filter: ["==", "iso_3166_1_alpha_3", iso],
    });

    if (!features || features.length === 0) {
      console.warn(`No features found for country: ${iso}`);
      return null;
    }

    const country = features[0];
    const bounds = this.calculateCountryBounds(country.geometry);
    this.countryBoundsCache[iso] = bounds;
    return bounds;
  }

  categorizeFeaturesByCountry() {
    const { originalFeatures } = this.state;
    const countryFeaturesByIso: { [key: string]: any[] } = {};

    if (!originalFeatures) return;

    // Filter out countries with null or invalid IDs
    const validCountries = this.props.highlightedCountries?.filter(
      (country) => country && country.country_id != null && country.country_id !== ""
    );

    if (!validCountries || validCountries.length === 0) {
      console.log("categorizeFeaturesByCountry: No valid countries to process");
      return;
    }

    validCountries.forEach((country) => {
      const bounds = this.getCountryBounds(country.country_id);

      if (!bounds) {
        countryFeaturesByIso[country.country_id] = [];
        return;
      }

      countryFeaturesByIso[country.country_id] = originalFeatures.filter((feature: any) => {
        if (!feature || !feature.properties) {
          return false;
        }

        const [lng, lat] = feature.geometry.coordinates;
        const [west, south, east, north] = bounds;

        return lng >= west && lng <= east && lat >= south && lat <= north;
      });
    });

    this.setState({ countryFeaturesByIso });
  }

  setupEventHandlersForSource(sourceId: string) {
    console.log(`Setting up event handlers for source: ${sourceId}`);

    // CRITICAL: Always clean up first to prevent duplicates
    this.cleanupEventHandlers(sourceId);

    const clusterLayerId = getLayerId(layers.clusters, sourceId);
    const unclusteredLayerId = getLayerId(layers.unclusteredPoint, sourceId);

    // For filter-clustered source, use more retries and longer delays due to async layer creation
    const maxRetries = sourceId === 'filter-clustered' ? 8 : 3;
    const retryDelay = sourceId === 'filter-clustered' ? 300 : 200;

    const checkAndSetupHandlers = (retryCount = 0) => {
      // Check if source exists first
      if (!this.map.getSource(sourceId)) {
        console.warn(`Source ${sourceId} not found, skipping event handler setup`);
        return;
      }

      if (!this.map.getLayer(clusterLayerId) || !this.map.getLayer(unclusteredLayerId)) {
        if (retryCount < maxRetries) {
          console.warn(`Layers not found for source ${sourceId}, retrying... (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => checkAndSetupHandlers(retryCount + 1), retryDelay);
          return;
        } else {
          console.error(`Failed to find layers for source ${sourceId} after ${maxRetries} retries`);
          console.log(`Expected layer IDs: ${clusterLayerId}, ${unclusteredLayerId}`);
          console.log(`Available layers:`, this.map.getStyle().layers?.map((l: any) => l.id));
          return;
        }
      }

      // Set up cluster click handler
      const clusterHandler = (e: any) => {
        console.log(`Cluster clicked for source: ${sourceId}`);
        e.originalEvent?.stopPropagation();
        this.onClusterClick(e, layers.clusters, sourceId);
      };
      this.storeHandlerRef(`cluster-${clusterLayerId}`, clusterHandler);
      this.map.on("click", clusterLayerId, clusterHandler);

      // `openPopup()` owns the duplicate-click guard, so the handler is registered as is: the extra
      // wrapper that used to sit here added a second guard that only ran after the first call had
      // already done its selection work.
      this.storeHandlerRef(`popup-${unclusteredLayerId}`, this.openPopup);
      this.map.on("click", unclusteredLayerId, this.openPopup);

      // Set up mouse events
      const enterHandler = () => {
        this.map.getCanvas().style.cursor = "pointer";
      };
      const leaveHandler = () => {
        this.map.getCanvas().style.cursor = "";
      };

      [clusterLayerId, unclusteredLayerId].forEach(layerId => {
        this.storeHandlerRef(`enter-${layerId}`, enterHandler);
        this.storeHandlerRef(`leave-${layerId}`, leaveHandler);
        this.map.on("mouseenter", layerId, enterHandler);
        this.map.on("mouseleave", layerId, leaveHandler);
      });

      console.log(`Event handlers set up successfully for source: ${sourceId}`);
    };

    checkAndSetupHandlers();
  }

  /**
   * Waits for the tree features of a country to be handed over by the parent. On the public map
   * they are the only source of clusters (`props.geoJsonFiles` stays empty, so nothing is
   * categorised locally).
   *
   * There is deliberately no timeout: a country with a heavy payload (Uganda: ~16.7 MB, ~4-5s) is
   * always waited for, never skipped. The wait is only ever ended by a fact - the features arrive,
   * the selection changes (Back or another country), or `props.countryDataLoading` reports that
   * the parent's request has settled without data (which is how a failed request ends it).
   * `componentDidUpdate` additionally renders the features if they land after the view was left.
   */
  private async waitForCountryFeatures(iso: string): Promise<any[]> {
    const read = (): any[] => {
      const fromState = this.state.countryFeaturesByIso[iso];
      if (fromState && fromState.length > 0) {
        return fromState;
      }
      const propsData = this.props.countrySpecificData as any;
      if (propsData && Array.isArray(propsData.features) && propsData.features.length > 0) {
        return propsData.features;
      }
      return [];
    };

    let features = read();

    while (
      features.length === 0 &&
      this.selectedCountryId === iso &&
      this.props.countryDataLoading !== false
    ) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      features = read();
    }

    if (features.length === 0) {
      console.warn(`Country data for ${iso} was not provided by the parent`);
    }

    return features;
  }

  async showTreesForCountry(countryIso: string) {
    const { countryFeaturesByIso } = this.state;


    // Wait for features to be available (either in state cache or in props)
    // This prevents the method from returning early before clusters can be rendered.
    let activeCountryFeatures = countryFeaturesByIso[countryIso] || [];
    if (activeCountryFeatures.length === 0) {
      const propsData = this.props.countrySpecificData as any;
      if (propsData && propsData.features && Array.isArray(propsData.features)) {
        activeCountryFeatures = propsData.features;
      }
    }

    if (activeCountryFeatures.length === 0 && this.props.public) {
      activeCountryFeatures = await this.waitForCountryFeatures(countryIso);
    }

    if (!countryFeaturesByIso[countryIso] && activeCountryFeatures.length === 0) {
      const propsData = this.props.countrySpecificData as any;
      const hasPropsData = propsData && propsData.features && Array.isArray(propsData.features) && propsData.features.length > 0;

      if (!hasPropsData || this.props.selectedCountryId !== countryIso) {
        console.warn(`No features found for country: ${countryIso}`);
        return;
      }
    }

    // This call can be the tail of a selection that is already over: the user pressed Back (or
    // opened another country) while this country's payload was still on its way, and the method
    // resumed when it landed. `selectedCountryId` is the field that transition clears
    // synchronously, so it is the fact to trust here - the props lag behind it.
    // Bowing out is what keeps Back final. This used to
    // `setState({ selectedCountryId: countryIso, showBackButton: true })`, i.e. put the country
    // view back: the Back button reappeared over the world view and, because
    // `isCountryViewOpen()` then reported an open country again, the country counts that the
    // Back transition was about to display were skipped - the world view came back without its
    // badges (they are hidden when a country is opened).
    if (this.selectedCountryId !== countryIso) {
      console.log(`showTreesForCountry: ${countryIso} is no longer the selected country, skipping`);
      return;
    }

    this.hideAllTreeLayers(countryIso);

    // Reuse the features we already found/waited for at the start of the function
    if (activeCountryFeatures.length === 0) {
      activeCountryFeatures = countryFeaturesByIso[countryIso] || [];
    }

    if (activeCountryFeatures.length === 0) {
      const propsData = this.props.countrySpecificData as any;
      if (propsData && propsData.features && Array.isArray(propsData.features)) {
        activeCountryFeatures = propsData.features;
      }
    }

    if (activeCountryFeatures.length === 0) {
      console.warn(`No tree features for country: ${countryIso}`);
      // Safety: ensure we don't get stuck in a loading state if we really have no features
      return;
    }

    const sourceId = `country-trees-${countryIso}`;
    // `resetToInitialCondition()` removes the per-country layers when the country view is
    // left, but it keeps their source alive. Layers used to be created only when the source
    // was missing, so from the second visit on this source had no layer at all: nothing was
    // rendered for it, `waitForTreesToRender()` never found a single feature and always fell
    // back to its 10s timeout, which left "Loading data..." on screen long after the clusters
    // (drawn by the generic `country-trees` source) had been displayed.
    const hasTreeLayers = !!this.map.getLayer(getLayerId(layers.clusters, sourceId));

    if (this.map.getSource(sourceId)) {
      console.log(`Updating existing source and showing layers for: ${sourceId}`);

      const source: any = this.map.getSource(sourceId);
      source.setData({
        type: "FeatureCollection",
        features: activeCountryFeatures,
      });
    } else {
      console.log(`Adding new source and layers for: ${sourceId}`);
      this.map.addSource(sourceId, {
        type: "geojson",
        maxzoom: sourceMaxZoom,
        data: {
          type: "FeatureCollection",
          features: activeCountryFeatures,
        },
        cluster: true,
        clusterMinPoints,
        clusterMaxZoom,
        clusterRadius: 30,
        clusterProperties: {
          treeCount: ["+", ["get", "numberPlanted"]],
        },
      });
    }

    // Rebuild the layers whenever they are missing. Partial leftovers are cleared first,
    // because `addLayer()` throws on an id that already exists.
    if (!hasTreeLayers) {
      this.removeLayers(sourceId);
      this.setLayersForSource(sourceId);
      this.setupEventHandlersForSource(sourceId);
    }

    // Force all related layers to be visible
    [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach((layerType) => {
      // 1. Check for country-specific source
      const countryLayerId = getLayerId(layerType, sourceId);
      if (this.map.getLayer(countryLayerId)) {
        this.map.setLayoutProperty(countryLayerId, "visibility", "visible");
      }

      // 2. The generic `country-trees` source carries the very same country data (it is what
      // `processSource()` builds from the API payload) and used to be forced visible here as
      // well. Both sets of clusters were drawn on top of each other, and since this source is
      // re-populated only after the zoom transition, its copy landed as a second render right
      // at the end of it. The dedicated source owns the country view, so the generic layers
      // stay hidden: they are the data carrier, not the renderer.
      const genericLayerId = getLayerId(layerType, "country-trees");
      if (this.map.getLayer(genericLayerId)) {
        this.map.setLayoutProperty(genericLayerId, "visibility", "none");
      }
    });

    requestAnimationFrame(() => {
      if (this.map) {
        this.map.triggerRepaint();
      }
    });

    // Wait for the trees to actually appear on screen before considering the task done
    // This ensures the loading overlay stays visible until clusters/trees are rendered
    await this.waitForTreesToRender(sourceId);

    this.setState({ treesVisible: true });
  }

  /**
   * Waits until the clusters of a country are actually rendered on screen. There is deliberately
   * no timeout: a country with a count badge always has trees, and the camera is fitted on the
   * country before this runs, so the clusters do render. Only a fact ends this wait - the
   * selection changing (Back or another country) - which also means it is always escapable.
   *
   * It resolves as soon as the clusters are drawn: it does not wait for the camera animation to
   * end, because the country data is prepared while that animation is still running.
   */
  private async waitForTreesToRender(sourceId: string): Promise<void> {
    if (!this.map) return;

    return new Promise((resolve) => {
      const check = () => {
        if (!this.map) {
          resolve();
          return;
        }

        // Selection safety check: ensure we are still waiting for the same country
        if (sourceId.startsWith('country-trees-')) {
          const isoFromSource = sourceId.replace('country-trees-', '');
          if (this.selectedCountryId !== isoFromSource) {
            resolve();
            return;
          }
        }

        // 1. Check if source is loaded
        const isSourceLoaded = this.map.isSourceLoaded(sourceId);

        // 2. Visual confirmation: are features actually rendered on screen?
        const treeLayers = [
          getLayerId(layers.clusters, sourceId),
          getLayerId(layers.unclusteredPoint, sourceId),
          getLayerId(layers.selectedUnclusteredPoint, sourceId)
        ].filter(id => {
          if (!this.map.getLayer(id)) return false;
          try {
            return this.map.getLayoutProperty(id, 'visibility') !== 'none';
          } catch (e) {
            return true;
          }
        });

        let featuresCount = 0;
        if (treeLayers.length > 0) {
          try {
            featuresCount = this.map.queryRenderedFeatures({ layers: treeLayers }).length;
          } catch (e) {
            // Ignore temporary errors
          }
        }


        // The clusters being drawn is what counts. Requiring the whole map to be idle as well
        // meant waiting for the camera animation (and for every background tile) to finish, which
        // kept the overlay up after the country was already displayed - the "few trees" case,
        // where the clusters appear well before the zoom-in ends.
        if (isSourceLoaded && featuresCount > 0) {
          // One final frame to ensure browser paint
          requestAnimationFrame(() => resolve());
          return;
        }

        // Wait for the next 'idle' event - the most reliable Mapbox signal for "done rendering"
        // We add a fallback setTimeout in case the idle event doesn't fire promptly
        let idleFired = false;
        const idleFallback = setTimeout(() => {
          if (!idleFired) {
            check();
          }
        }, 500);

        this.map.once('idle', () => {
          idleFired = true;
          clearTimeout(idleFallback);
          // Tiny delay to let browser finish painting
          setTimeout(check, 50);
        });
      };

      check();
    });
  }

  /**
   * Resolves once the map has rendered `frames` frames, plus one browser frame so that the last one
   * is composited. This is how a layer that was just changed (the popup's selection shadow, the
   * country counts after a Back) is waited for, instead of guessing with a fixed delay.
   */
  private async waitForMapRender(frames: number = 1): Promise<void> {
    if (!this.map) return;

    for (let i = 0; i < frames; i++) {
      await new Promise<void>((resolve) => this.map.once("render", () => resolve()));
    }

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  /**
   * Resolves on the frame that shows the country count badges: `showCountryCounts()` has just
   * switched their layers to visible, and that change is drawn by the map's next frame.
   *
   * This replaces polling `queryRenderedFeatures` on the badge layers: that only ever reported the
   * badges inside the viewport, so it needed a budget for the case where none was, and waiting
   * could not help there anyway - only what the map draws is reported.
   */
  private async waitForCountryBadgesToRender(): Promise<void> {
    await this.waitForMapRender(1);
  }

  /**
   * Puts the world view back in place: re-highlights the countries, rebuilds their tree counts and
   * resolves once those counts are painted, so an overlay can be removed with them on screen.
   * Owned by the two flows that clear the country data - the Back transition and closing the
   * country that is already open - because that is where the overlay waits for the counts.
   */
  private async refreshWorldViewCounts(): Promise<void> {
    try {
      await this.autoHighlightCountries();
      this.showCountryCounts();
    } catch (error) {
      console.warn('Error while re-highlighting countries:', error);
    }

    await this.waitForCountryBadgesToRender();
  }

  hideAllTreeLayers(excludeIso?: string | null) {
    const { geoJsonFiles } = this.props;

    // 1. Hide global tree layers
    // We always hide global trees when entering or switching country views
    geoJsonFiles.forEach((file) => {
      [layers.clusters, layers.clusterCount, layers.unclusteredPoint].forEach(
        (layerType) => {
          const layerId = getLayerId(layerType, file.name);
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, "visibility", "none");
          }
        }
      );
    });

    // 2. Hide country-specific layers
    this.props.highlightedCountries?.forEach((country) => {
      const sourceId = `country-trees-${country.country_id}`;

      // Never hide the country we want to keep visible (excludeIso)
      if (excludeIso && country.country_id === excludeIso) {
        return;
      }

      [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach(
        (layerType) => {
          const layerId = getLayerId(layerType, sourceId);
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, "visibility", "none");
          }
        }
      );
    });

    // 3. Handle generic country-trees source.
    // On a full hide (no excludeIso) it is hidden here. When a country is excluded the
    // dedicated source owns the view, so `showTreesForCountry` hides these layers right
    // after this call - the generic source is only a data carrier on the public map.
    const genericSourceId = "country-trees";
    if (!excludeIso) {
      [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach(
        (layerType) => {
          const layerId = getLayerId(layerType, genericSourceId);
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, "visibility", "none");
          }
        }
      );
    }

    // Only update treesVisible state if we are hiding EVERYTHING
    if (!excludeIso) {
      this.setState({ treesVisible: false });
    }
  }

  showAllTrees() {
    const { geoJsonFiles } = this.props;

    geoJsonFiles.forEach((file) => {
      [layers.clusters, layers.clusterCount, layers.unclusteredPoint].forEach(
        (layerType) => {
          const layerId = getLayerId(layerType, file.name);
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, "visibility", "visible");
          }
        }
      );
    });

    this.setState({ treesVisible: true });
  }

  /**
   * True while a country view is open, i.e. between the badge click and the Back button.
   * Country count badges belong to the world view only. `autoHighlightCountries()` has fixed
   * internal delays and the Back transition does not await it, so it can still be creating
   * and showing badges after the user has already opened another country.
   */
  private isCountryViewOpen(): boolean {
    return this.selectedCountryId !== null;
  }

  hideCountryCounts() {
    this.props.highlightedCountries?.forEach((country) => {
      const countLyr = `country-count-layer-${country.country_id}`;
      const countLyrBg = `${countLyr}-bg`;

      try {
        if (this.map.getLayer(countLyr)) {
          this.map.setLayoutProperty(countLyr, "visibility", "none");
        }
        if (this.map.getLayer(countLyrBg)) {
          this.map.setLayoutProperty(countLyrBg, "visibility", "none");
        }
      } catch (error) {
        console.warn(`Error hiding country count for ${country.iso}:`, error);
      }
    });
  }

  showCountryCounts() {
    // Badge work started by a Back transition can still be in flight here. Showing the badges
    // then left a few of them on screen over the country view until that transition ended
    // (seconds later, once the clusters of the clicked country were displayed): the country
    // view wins, and the badges are shown by the next Back transition.
    if (this.isCountryViewOpen()) {
      console.log("showCountryCounts: a country view is open, keeping the badges hidden");
      return;
    }

    this.props.highlightedCountries?.forEach((country) => {
      const countLyr = `country-count-layer-${country.country_id}`;
      const countLyrBg = `${countLyr}-bg`;

      try {
        if (this.map.getLayer(countLyr)) {
          this.map.setLayoutProperty(countLyr, "visibility", "visible");
          this.map.setPaintProperty(countLyr, "text-opacity", 1);
        }
        if (this.map.getLayer(countLyrBg)) {
          this.map.setLayoutProperty(countLyrBg, "visibility", "visible");
          this.map.setPaintProperty(countLyrBg, "circle-opacity", 0.9);
          this.map.setPaintProperty(countLyrBg, "circle-stroke-opacity", 1);
        }

        // Ensure the country boundary is highlighted when showing the count
        this.ensureCountryBoundaryHighlighted(country.country_id);
      } catch (error) {
        console.warn(`Error showing country count for ${country.iso}:`, error);
      }
    });
  }

  async addCountriesLayer() {
    this.map.addSource("countries", {
      type: "vector",
      url: "mapbox://mapbox.country-boundaries-v1",
    });

    this.setState({ loading: true });
    this.props.onLoadingStateChange?.(true);

    this.map.addLayer({
      id: layers.countries,
      type: "fill",
      source: "countries",
      "source-layer": "country_boundaries",
      paint: {
        "fill-color": "rgba(0, 0, 0, 0)",
        "fill-outline-color": "rgba(255, 255, 255, 0.2)",
      },
    });

    this.map.addLayer({
      id: layers.countriesHighlight,
      type: "line",
      source: "countries",
      "source-layer": "country_boundaries",
      paint: {
        "line-color": "#fff",
        "line-width": 3,
        "line-opacity": 1,
      },
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      filter: ["==", "iso_3166_1_alpha_3", ""],
    });

    this.map.on("mouseenter", layers.countries, () => {
      this.map.getCanvas().style.cursor = "default";
    });

    this.map.on("mouseleave", layers.countries, () => {
      this.map.getCanvas().style.cursor = "";
    });
  }

  async autoHighlightCountries() {
    // The world view highlight belongs to the world view only: while a country is open, the
    // highlight filter and the badges belong to that country, so a refresh left over from the
    // Back transition is skipped here and picked up by the next Back transition.
    if (this.isCountryViewOpen()) {
      console.log("autoHighlightCountries: a country view is open, skipping");
      return;
    }

    if (!this.props.public || !this.props.highlightedCountries || this.props.highlightedCountries.length === 0) {
      console.log("autoHighlightCountries: No highlighted countries data available");
      return;
    }

    console.log("autoHighlightCountries: Processing", this.props.highlightedCountries.length, "countries");

    // Filter out any null or undefined country_ids
    const validCountries = this.props.highlightedCountries!.filter(
      (country) => country && country.country_id != null && country.country_id !== ""
    );

    if (validCountries.length === 0) {
      console.log("autoHighlightCountries: No valid country IDs found");
      return;
    }

    console.log("autoHighlightCountries: Found", validCountries.length, "valid countries");

    return new Promise<void>((resolve) => {
      const checkAndApplyHighlight = (retryCount = 0) => {
        // Check if the countries layer exists and is ready
        if (!this.map.getLayer(layers.countries) || !this.map.getLayer(layers.countriesHighlight)) {
          if (retryCount < 10) {
            console.log(`autoHighlightCountries: Layers not ready, retrying... (${retryCount + 1}/10)`);
            setTimeout(() => checkAndApplyHighlight(retryCount + 1), 200);
            return;
          } else {
            console.error("autoHighlightCountries: Countries layers not found after 10 retries");
            resolve();
            return;
          }
        }

        // Layers are ready, apply the highlight
        setTimeout(() => {
          // A country view may have been opened during the retries and the delay above, in
          // which case highlighting every country now would draw borders and badges over it.
          if (this.isCountryViewOpen()) {
            console.log("autoHighlightCountries: a country view is open, skipping");
            resolve();
            return;
          }

          const features = this.map.querySourceFeatures("countries", {
            sourceLayer: "country_boundaries",
            filter: [
              "in",
              "iso_3166_1_alpha_3",
              ...validCountries.map((c) => c.country_id),
            ],
          });

          console.log("autoHighlightCountries: Found", features?.length || 0, "country features");

          if (features && features.length > 0) {
            const countryFilters = validCountries.map((c) => c.country_id);

            this.map.setFilter(layers.countriesHighlight, [
              "in",
              "iso_3166_1_alpha_3",
              ...countryFilters,
            ]);

            this.highlightedCountries = countryFilters;

            const countPromises = validCountries.map((countryData) =>
              this.addCountryCountPoint(countryData.country_id, countryData.trees_count)
            );

            Promise.all(countPromises).then(() => {
              console.log("autoHighlightCountries: All country count points added");
              resolve();
            });
          } else {
            console.log("autoHighlightCountries: No features found, resolving");
            resolve();
          }
        }, 500); // Additional delay to ensure source is fully loaded
      };

      checkAndApplyHighlight();
    });
  }

  async addCountryCountPoint(iso: string, count: number): Promise<void> {
    return new Promise((resolve) => {
      try {
        // A country view may have been opened while these badges were being prepared: badges
        // are added visible, so creating this one now would draw it over the country view
        // until that transition ends. It is created again by the next Back transition.
        if (this.isCountryViewOpen()) {
          console.log(`Skipping country count for ${iso}: a country view is open`);
          resolve();
          return;
        }

        console.log(`Processing country: ${iso}`);

        // Check if we already have cached center coordinates
        let centerCoords: [number, number] | null = this.countryCenterCache[iso];

        if (centerCoords && this.isValidCoordinate(centerCoords)) {
          console.log(`Using cached center for ${iso}:`, centerCoords);
          this.createCountryCountLayer(iso, count, centerCoords);
          setTimeout(() => resolve(), 100);
          return;
        }

        // Calculate center coordinates if not cached
        centerCoords = this.getCountryCenterFromRenderedFeatures(iso);

        if (centerCoords && this.isValidCoordinate(centerCoords)) {
          console.log(`Using rendered features center for ${iso}:`, centerCoords);
          this.countryCenterCache[iso] = centerCoords; // Cache the coordinates
          this.createCountryCountLayer(iso, count, centerCoords);
          setTimeout(() => resolve(), 100);
          return;
        }

        const boundsCenter = this.getAdvancedBoundsCenter(iso);

        if (boundsCenter && this.isValidCoordinate(boundsCenter)) {
          console.log(`Using advanced bounds center for ${iso}:`, boundsCenter);
          this.countryCenterCache[iso] = boundsCenter; // Cache the coordinates
          this.createCountryCountLayer(iso, count, boundsCenter);
          setTimeout(() => resolve(), 100);
          return;
        }

        console.warn(`Could not calculate center for ${iso}`);
        resolve();

      } catch (error) {
        console.error(`Error adding country count point for ${iso}:`, error);
        resolve();
      }
    });
  }

  getAdvancedBoundsCenter(iso: string): [number, number] | null {
    try {
      const bounds = this.getCountryBounds(iso);
      if (!bounds) {
        return null;
      }

      const [west, south, east, north] = bounds;
      const width = east - west;
      const height = north - south;
      const aspectRatio = width / height;

      let centerLng = (west + east) / 2;
      let centerLat = (south + north) / 2;

      if (aspectRatio > 3) {
        centerLat = south + (height * 0.4);
      } else if (aspectRatio < 0.33) {
        centerLng = west + (width * 0.4);
      }

      const mainlandCenter = this.findMainlandCenter(iso, bounds);
      if (mainlandCenter) {
        return mainlandCenter;
      }

      return [centerLng, centerLat];

    } catch (error) {
      console.warn(`Error calculating advanced bounds center for ${iso}:`, error);
      return null;
    }
  }

  /**
   * The point Mapbox prints the country's name at, read from the `place_label` tiles. It is used to
   * tell which of a country's tile fragments is the country (see
   * `getCountryCenterFromRenderedFeatures`), and deliberately *not* as the badge position: it is
   * placed for text, so it sits off the middle of the shape (France: Orléans, 137 km north of the
   * centroid; Spain: 118 km north of it, at Valladolid). What it is good at is being unambiguously on
   * the country - its mainland, never an overseas territory, never at sea.
   *
   * There is exactly one distinct anchor per country in the loaded tiles (verified on preprod), so the
   * first feature is enough. Under 5 ms per call, once per country.
   */
  getCountryLabelAnchor(iso: string): [number, number] | null {
    const twoDigitCode = this.getTwoDigitIsoCode(iso);

    if (!twoDigitCode) {
      return null;
    }

    try {
      const features = this.map.querySourceFeatures("composite", {
        sourceLayer: "place_label",
        filter: ["all", ["==", "class", "country"], ["==", "iso_3166_1", twoDigitCode]],
      });

      const anchor = features && features[0] && features[0].geometry && features[0].geometry.coordinates;

      return this.isValidCoordinate(anchor) ? [anchor[0], anchor[1]] : null;

    } catch (error) {
      // A style without the `place_label` source layer, or tiles that are not loaded yet: the badge
      // is then placed by the country's geometry, as it was before.
      console.warn(`Could not read the country label anchor for ${iso}:`, error);
      return null;
    }
  }

  /**
   * The box the country view is fitted to: the country's **main landmass**.
   *
   * Not the country's bounds as Mapbox hands them over. The geometry arrives clipped per tile, so a
   * country that straddles a tile edge comes as several fragments, and the country view used to be
   * fitted to one of them: France's eastern half, which cut Brittany, Aquitaine and the west coast out
   * of the frame.
   *
   * Not the union of everything either: the same tile that carries Spain's mainland carries the
   * Canaries 1000 km away, and fitting those shrinks the mainland to a corner of the map. So only the
   * pieces near the country's main landmass are kept - the ones whose centre is within half of that
   * landmass' own diagonal from its box. Measured: that keeps Corsica (68 km from mainland France),
   * the Balearics (250 km from mainland Spain) and Zanzibar, and drops the Canaries (900 km away),
   * Alaska, Hawaii and the DOM-TOM.
   */
  getCountryMainLandmassBounds(iso: string): [number, number, number, number] | null {
    try {
      const fragments = this.map.querySourceFeatures("countries", {
        sourceLayer: "country_boundaries",
        filter: ["==", "iso_3166_1_alpha_3", iso],
      });

      if (!fragments || fragments.length === 0) {
        console.warn(`No features found for country: ${iso}`);
        return null;
      }

      const rectangles: { bounds: [number, number, number, number]; centre: [number, number] }[] = [];

      fragments.forEach((fragment: any) => {
        const geometry = fragment.geometry;
        if (!geometry || !geometry.coordinates) {
          return;
        }

        const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;

        polygons.forEach((polygon: any) => {
          const ring = polygon && polygon[0];
          if (!ring || ring.length === 0) {
            return;
          }

          const longitudes = ring.map((point: number[]) => point[0]);
          const latitudes = ring.map((point: number[]) => point[1]);
          const bounds: [number, number, number, number] = [
            Math.min(...longitudes),
            Math.min(...latitudes),
            Math.max(...longitudes),
            Math.max(...latitudes),
          ];

          rectangles.push({ bounds, centre: [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2] });
        });
      });

      if (rectangles.length === 0) {
        return null;
      }

      const boxArea = (rectangle: { bounds: [number, number, number, number] }) =>
        (rectangle.bounds[2] - rectangle.bounds[0]) * (rectangle.bounds[3] - rectangle.bounds[1]);

      // The main landmass is the biggest piece the tiles hand over. Its own size is then the scale of
      // "nearby": a piece whose centre sits further from that piece than half its diagonal is another
      // landmass, not part of this country view.
      const main = rectangles.reduce((largest, rectangle) =>
        boxArea(rectangle) > boxArea(largest) ? rectangle : largest
      );

      const nearbyLimit =
        this.calculateDistance(main.bounds[1], main.bounds[0], main.bounds[3], main.bounds[2]) / 2;

      const kept = [main, ...rectangles.filter((rectangle) =>
        rectangle !== main && this.distancePointToBoundsKm(rectangle.centre, main.bounds) <= nearbyLimit
      )];

      return [
        Math.min(...kept.map((rectangle) => rectangle.bounds[0])),
        Math.min(...kept.map((rectangle) => rectangle.bounds[1])),
        Math.max(...kept.map((rectangle) => rectangle.bounds[2])),
        Math.max(...kept.map((rectangle) => rectangle.bounds[3])),
      ];

    } catch (error) {
      console.warn(`Could not work out the landmass bounds of ${iso}:`, error);
      return null;
    }
  }

  /** How far a point sits from a box, in km; 0 when it is inside it. */
  private distancePointToBoundsKm(
    point: [number, number],
    bounds: [number, number, number, number]
  ): number {
    const longitude = Math.min(Math.max(point[0], bounds[0]), bounds[2]);
    const latitude = Math.min(Math.max(point[1], bounds[1]), bounds[3]);

    return this.calculateDistance(point[1], point[0], latitude, longitude);
  }

  getCountryCenterFromRenderedFeatures(iso: string) {
    try {
      const allFeatures = this.map.queryRenderedFeatures({
        layers: [layers.countries]
      });

      const fragments = allFeatures.filter((feature: any) =>
        feature.properties &&
        feature.properties.iso_3166_1_alpha_3 === iso
      );

      if (fragments.length === 0) {
        return null;
      }

      // One fragment: the country is whole in the tiles this view holds, so its geometry is the
      // country - a point inside its biggest polygon, which is the mainland (see
      // `calculateGeometryCenter`), whatever islands or overseas territories the rest of it carries.
      if (fragments.length === 1) {
        return this.calculateGeometryCenter(fragments[0].geometry);
      }

      // Several: Mapbox clipped the country at the tile edges, so each fragment only holds the part of
      // it that falls inside its tile (+ the tile's buffer) and the fragments overlap. Taking the
      // first of them put France's badge 129 km west of the country - its western half - because a
      // badge has to stand for the country, not for a tile. Mapbox's own country label anchor says
      // where the country is, so the fragment whose centre is nearest to the anchor is the one kept.
      const anchor = this.getCountryLabelAnchor(iso);

      if (!anchor) {
        return this.calculateGeometryCenter(fragments[0].geometry);
      }

      let center = this.calculateGeometryCenter(fragments[0].geometry);
      let distanceToAnchor = this.calculateDistance(center[1], center[0], anchor[1], anchor[0]);

      fragments.slice(1).forEach((fragment: any) => {
        const candidate = this.calculateGeometryCenter(fragment.geometry);
        const distance = this.calculateDistance(candidate[1], candidate[0], anchor[1], anchor[0]);

        if (distance < distanceToAnchor) {
          distanceToAnchor = distance;
          center = candidate;
        }
      });

      console.log(`Country ${iso} came clipped in ${fragments.length} fragments; kept the one ${Math.round(distanceToAnchor)} km from its label anchor`);

      return center;

    } catch (error) {
      console.warn(`Error getting center from rendered features for ${iso}:`, error);
      return null;
    }
  }

  isPointInPolygonBounds(point: [number, number], coordinates: number[][][]): boolean {
    const bounds = this.calculatePolygonBounds(coordinates);
    const [x, y] = point;
    const [minX, minY, maxX, maxY] = bounds;
    return x >= minX && x <= maxX && y >= minY && y <= maxY;
  }

  calculatePoleOfInaccessibility(coordinates: number[][][]): [number, number] | null {
    const ring = coordinates[0];
    const bounds = this.calculatePolygonBounds(coordinates);
    const [minX, minY, maxX, maxY] = bounds;

    const cellSize = Math.min(maxX - minX, maxY - minY) / 10;
    let bestDistance = -1;
    let bestPoint: [number, number] | null = null;

    for (let x = minX; x <= maxX; x += cellSize) {
      for (let y = minY; y <= maxY; y += cellSize) {
        const point: [number, number] = [x, y];

        if (this.isPointInPolygon(point, ring)) {
          const distance = this.distanceToPolygonEdge(point, ring);
          if (distance > bestDistance) {
            bestDistance = distance;
            bestPoint = point;
          }
        }
      }
    }

    return bestPoint;
  }

  isPointInPolygon(point: [number, number], ring: number[][]): boolean {
    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }

  distanceToPolygonEdge(point: [number, number], ring: number[][]): number {
    const [px, py] = point;
    let minDistance = Infinity;

    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];

      const distance = this.pointToLineDistance([px, py], [x1, y1], [x2, y2]);
      minDistance = Math.min(minDistance, distance);
    }

    return minDistance;
  }

  pointToLineDistance(point: [number, number], lineStart: [number, number], lineEnd: [number, number]): number {
    const [px, py] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;

    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
      return Math.sqrt(A * A + B * B);
    }

    const param = dot / lenSq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  calculateCountryVisualCenter(geometry: any): [number, number] {
    if (geometry.type === "Polygon") {
      return this.calculatePolygonVisualCenter(geometry.coordinates);
    } else if (geometry.type === "MultiPolygon") {
      return this.calculateMultiPolygonVisualCenter(geometry.coordinates);
    }
    return this.calculateBoundingBoxCenter(geometry);
  }

  isValidCoordinate(coord: any) {
    return coord &&
      coord.length === 2 &&
      isFinite(coord[0]) &&
      isFinite(coord[1]) &&
      coord[0] >= -180 && coord[0] <= 180 &&
      coord[1] >= -90 && coord[1] <= 90;
  }

  ensureCountryBoundaryHighlighted(iso: string) {
    // Validate the iso parameter
    if (!iso || iso === null || iso === undefined) {
      console.warn("ensureCountryBoundaryHighlighted: Invalid ISO code provided");
      return;
    }

    // Check if the countries highlight layer exists
    if (!this.map.getLayer(layers.countriesHighlight)) {
      console.warn(`ensureCountryBoundaryHighlighted: ${layers.countriesHighlight} layer not found`);
      return;
    }

    // Get current filter
    const currentFilter = this.map.getFilter(layers.countriesHighlight);

    // If no filter is set or this country is not in the filter, update it
    if (!currentFilter || currentFilter.length < 3 || !currentFilter[2].includes(iso)) {
      // Get all currently highlighted countries and filter out nulls
      const highlightedCountries = (this.highlightedCountries || []).filter(
        (country) => country != null && country !== ""
      );

      // Add this country if not already present
      if (!highlightedCountries.includes(iso)) {
        highlightedCountries.push(iso);
        this.highlightedCountries = highlightedCountries;
      }

      // Update the filter to include all highlighted countries
      this.map.setFilter(layers.countriesHighlight, [
        "in",
        "iso_3166_1_alpha_3",
        ...highlightedCountries,
      ]);

      console.log(`ensureCountryBoundaryHighlighted: Added ${iso} to highlighted countries`);
    }
  }

  createCountryCountLayer(iso: any, count: any, centerCoords: any) {
    const countSrc = `country-count-${iso}`;
    const countLyr = `country-count-layer-${iso}`;

    const countGeojson = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: centerCoords,
          },
          properties: {
            count: count.toLocaleString(),
            iso: iso,
            name: iso,
          },
        },
      ],
    };

    const existingCountSource: any = this.map.getSource(countSrc);
    const hasBadgeLayers =
      !!this.map.getLayer(`${countLyr}-bg`) && !!this.map.getLayer(countLyr);

    // Update the badge in place when it already exists to avoid render it a second time.
    if (existingCountSource && hasBadgeLayers) {
      existingCountSource.setData(countGeojson);
      this.ensureCountryBoundaryHighlighted(iso);
      // Keep the badge on top of the map layers, which the remove/add cycle used to give.
      this.map.moveLayer(`${countLyr}-bg`);
      this.map.moveLayer(countLyr);
      return;
    }

    // Otherwise rebuild it, clearing any leftovers first (addLayer() throws on an existing id).
    [`${countLyr}-bg`, countLyr].forEach((layerId) => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });
    if (existingCountSource) {
      this.map.removeSource(countSrc);
    }

    this.map.addSource(countSrc, {
      type: "geojson",
      data: countGeojson
    });

    this.map.addLayer({
      id: `${countLyr}-bg`,
      type: "circle",
      source: countSrc,
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          1, 15,
          3, 22,
          5, 28,
          8, 34,
          12, 40,
        ],
        "circle-color": "#007cbf",
        "circle-stroke-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          1, 2,
          5, 3,
          10, 4,
        ],
        "circle-stroke-color": "#ffffff",
        "circle-opacity": 1,
        "circle-stroke-opacity": 1,
        "circle-pitch-alignment": "viewport",
        "circle-pitch-scale": "viewport",
      },
    });

    this.map.addLayer({
      id: countLyr,
      type: "symbol",
      source: countSrc,
      layout: {
        "text-field": ["get", "count"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          1, 10,
          3, 12,
          5, 14,
          8, 18,
          12, 22,
        ],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-pitch-alignment": "viewport",
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "rgba(0,0,0,0.8)",
        "text-halo-width": 2,
      },
    });

    // Ensure this country's boundary is highlighted when the count layer is created
    this.ensureCountryBoundaryHighlighted(iso);

    [`${countLyr}-bg`, countLyr].forEach((layerId) => {
      // These layers are removed and re-added on every refresh (`autoHighlightCountries`
      // is called again on each Back), but Mapbox keeps layer-scoped listeners alive
      // after `removeLayer()`. Without clearing them first, every refresh stacked one
      // more set of handlers, so a single click ran `selectCountryDirectly()` several
      // times concurrently: each of those runs cleared the "Loading data..." state on
      // its own schedule, which is why only the very first click stayed in sync with
      // the tree clusters. Same off-before-on pattern as `setOpenPopupOnClick`.
      this.map.off("mouseenter", layerId);
      this.map.off("mouseleave", layerId);
      this.map.off("click", layerId);

      this.map.on("mouseenter", layerId, () => {
        this.map.getCanvas().style.cursor = "pointer";
      });

      this.map.on("mouseleave", layerId, () => {
        this.map.getCanvas().style.cursor = "";
      });

      this.map.on("click", layerId, (e: any) => {
        e.originalEvent?.preventDefault();
        // The badge is a circle plus its label and both callbacks run in the same tick, where
        // `this.state.loading` is still false for the second one (setState is asynchronous).
        // Without this synchronous guard the second callback re-ran the whole selection,
        // re-setting the same source data and rendering the clusters a second time.
        if (this.state.loading || this.countryBadgeClickProcessing) return;
        this.countryBadgeClickProcessing = true;
        setTimeout(() => {
          this.countryBadgeClickProcessing = false;
        }, 500);
        this.setState({ loading: true });
        this.props.onLoadingStateChange?.(true);
        this.selectCountryDirectly(iso);
      });
    });
  }

  calculatePolygonCenter(coordinates: any) {
    const ring = coordinates[0];
    return this.calculateCentroidOfRing(ring);
  }

  calculateMultiPointCenter(coordinates: any) {
    let totalLng = 0, totalLat = 0;
    coordinates.forEach(([lng, lat]: any) => {
      totalLng += lng;
      totalLat += lat;
    });
    return [totalLng / coordinates.length, totalLat / coordinates.length];
  }

  findMainlandCenter(iso: any, bounds: any): [number, number] | null {
    try {
      const [west, south, east, north] = bounds;
      const width = east - west;
      const height = north - south;

      const samplePoints = [];
      const gridSize = 5;

      for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
          const lng = west + (width * i / (gridSize - 1));
          const lat = south + (height * j / (gridSize - 1));

          const point = this.map.project([lng, lat]);
          const features = this.map.queryRenderedFeatures(point, {
            layers: [layers.countries]
          });

          const isInCountry = features.some((feature: any) =>
            feature.properties &&
            feature.properties.iso_3166_1_alpha_3 === iso
          );

          if (isInCountry) {
            samplePoints.push([lng, lat]);
          }
        }
      }

      if (samplePoints.length === 0) {
        return null;
      }

      let totalLng = 0, totalLat = 0;
      samplePoints.forEach(([lng, lat]) => {
        totalLng += lng;
        totalLat += lat;
      });

      return [totalLng / samplePoints.length, totalLat / samplePoints.length];

    } catch (error) {
      console.warn(`Error finding mainland center for ${iso}:`, error);
      return null;
    }
  }

  calculateGeometryCenter(geometry: any) {
    if (!geometry || !geometry.coordinates) {
      throw new Error('Invalid geometry');
    }

    switch (geometry.type) {
      case 'Point':
        return geometry.coordinates;
      case 'LineString':
        return this.calculateLineStringCenter(geometry.coordinates);
      case 'Polygon':
        return this.calculatePolygonCenter(geometry.coordinates);
      case 'MultiPoint':
        return this.calculateMultiPointCenter(geometry.coordinates);
      case 'MultiLineString':
        return this.calculateMultiLineStringCenter(geometry.coordinates);
      case 'MultiPolygon':
        return this.calculateMultiPolygonCenter(geometry.coordinates);
      default:
        throw new Error(`Unsupported geometry type: ${geometry.type}`);
    }
  }

  calculateMultiLineStringCenter(coordinates: any) {
    let longestLine = coordinates[0];
    let maxLength = longestLine.length;

    coordinates.forEach((line: any) => {
      if (line.length > maxLength) {
        maxLength = line.length;
        longestLine = line;
      }
    });

    return this.calculateLineStringCenter(longestLine);
  }

  calculateLineStringCenter(coordinates: any): [number, number] {
    const midIndex = Math.floor(coordinates.length / 2);
    const point = coordinates[midIndex];
    return [point[0], point[1]];
  }

  calculateMultiPolygonCenter(coordinates: any) {
    let largestPolygon = coordinates[0];
    let maxCoords = largestPolygon[0].length;

    coordinates.forEach((polygon: any) => {
      if (polygon[0].length > maxCoords) {
        maxCoords = polygon[0].length;
        largestPolygon = polygon;
      }
    });

    return this.calculatePolygonCenter(largestPolygon);
  }

  calculateCentroidOfRing(ring: any) {
    if (ring.length < 4) {
      throw new Error('Invalid ring');
    }

    let area = 0;
    let centroidX = 0;
    let centroidY = 0;

    for (let i = 0; i < ring.length - 1; i++) {
      const [x0, y0] = ring[i];
      const [x1, y1] = ring[i + 1];

      if (!isFinite(x0) || !isFinite(y0) || !isFinite(x1) || !isFinite(y1)) {
        continue;
      }

      const crossProduct = x0 * y1 - x1 * y0;
      area += crossProduct;
      centroidX += (x0 + x1) * crossProduct;
      centroidY += (y0 + y1) * crossProduct;
    }

    area *= 0.5;

    if (Math.abs(area) < 1e-10) {
      let totalX = 0, totalY = 0, validCount = 0;

      for (let i = 0; i < ring.length - 1; i++) {
        const [x, y] = ring[i];
        if (isFinite(x) && isFinite(y)) {
          totalX += x;
          totalY += y;
          validCount++;
        }
      }

      if (validCount === 0) {
        throw new Error('No valid coordinates found');
      }

      return [totalX / validCount, totalY / validCount];
    }

    centroidX /= (6 * area);
    centroidY /= (6 * area);

    if (!isFinite(centroidX) || !isFinite(centroidY)) {
      throw new Error('Calculated centroid contains invalid values');
    }

    return [centroidX, centroidY];
  }

  calculateMultiPolygonVisualCenter(coordinates: number[][][][]): [number, number] {
    let bestCenter: [number, number] | null = null;
    let bestScore = -1;

    for (const polygon of coordinates) {
      const polygonArea = this.calculatePolygonArea(polygon[0]);
      const polygonCenter = this.calculatePolygonCentroid(polygon);
      const score = polygonArea;

      if (score > bestScore) {
        bestScore = score;
        bestCenter = polygonCenter;
      }
    }

    return bestCenter || this.calculateBoundingBoxCenter({ coordinates });
  }

  calculatePolygonVisualCenter(coordinates: number[][][]): [number, number] {
    const ring = coordinates[0];

    if (ring.length < 4) {
      return [ring[0][0], ring[0][1]];
    }

    try {
      const centroid = this.calculatePolygonCentroid(coordinates);

      if (this.isPointInPolygonBounds(centroid, coordinates)) {
        return centroid;
      }
    } catch (error) {
      console.warn('Centroid calculation failed, falling back to other methods');
    }

    try {
      const visualCenter = this.calculatePoleOfInaccessibility(coordinates);
      if (visualCenter && this.isPointInPolygonBounds(visualCenter, coordinates)) {
        return visualCenter;
      }
    } catch (error) {
      console.warn('Pole of inaccessibility calculation failed');
    }

    const bounds = this.calculatePolygonBounds(coordinates);
    return [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2];
  }

  calculatePolygonCentroid(coordinates: number[][][]): [number, number] {
    const ring = coordinates[0];

    if (ring.length < 4) {
      throw new Error('Invalid polygon: insufficient points');
    }

    let area = 0;
    let centroidX = 0;
    let centroidY = 0;

    for (let i = 0; i < ring.length - 1; i++) {
      const [x0, y0] = ring[i];
      const [x1, y1] = ring[i + 1];

      if (!isFinite(x0) || !isFinite(y0) || !isFinite(x1) || !isFinite(y1)) {
        throw new Error('Invalid coordinates found in polygon');
      }

      const crossProduct = x0 * y1 - x1 * y0;
      area += crossProduct;
      centroidX += (x0 + x1) * crossProduct;
      centroidY += (y0 + y1) * crossProduct;
    }

    area *= 0.5;

    if (Math.abs(area) < 1e-10) {
      const avgX = ring.slice(0, -1).reduce((sum, coord) => sum + coord[0], 0) / (ring.length - 1);
      const avgY = ring.slice(0, -1).reduce((sum, coord) => sum + coord[1], 0) / (ring.length - 1);
      return [avgX, avgY];
    }

    centroidX /= (6 * area);
    centroidY /= (6 * area);

    if (!isFinite(centroidX) || !isFinite(centroidY)) {
      throw new Error('Calculated centroid contains invalid values');
    }

    return [centroidX, centroidY];
  }

  calculatePolygonBounds(coordinates: number[][][]): [number, number, number, number] {
    const ring = coordinates[0];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const [x, y] of ring) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }

    return [minX, minY, maxX, maxY];
  }

  calculatePolygonArea(ring: number[][]): number {
    let area = 0;

    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      area += (x1 * y2) - (x2 * y1);
    }

    return Math.abs(area) / 2;
  }

  calculateBoundingBoxCenter(geometry: any): [number, number] {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    const processCoordinates = (coords: any) => {
      if (Array.isArray(coords[0])) {
        coords.forEach(processCoordinates);
      } else {
        const [x, y] = coords;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    };

    processCoordinates(geometry.coordinates);
    return [(minX + maxX) / 2, (minY + maxY) / 2];
  }

  forceHideAllCountryLayers() {
    this.props.highlightedCountries!.forEach((country) => {
      const countLyr = `country-count-layer-${country.country_id}`;
      const countLyrBg = `${countLyr}-bg`;

      try {
        if (this.map.getLayer(countLyr)) {
          this.map.setLayoutProperty(countLyr, "visibility", "none");
        }
        if (this.map.getLayer(countLyrBg)) {
          this.map.setLayoutProperty(countLyrBg, "visibility", "none");
        }

        if (this.map.getLayer(countLyr)) {
          this.map.setPaintProperty(countLyr, "text-opacity", 0);
        }
        if (this.map.getLayer(countLyrBg)) {
          this.map.setPaintProperty(countLyrBg, "circle-opacity", 0);
          this.map.setPaintProperty(countLyrBg, "circle-stroke-opacity", 0);
        }
      } catch (error) {
        console.warn(
          `Error force hiding country layers for ${country.iso}:`,
          error
        );

        try {
          if (this.map.getLayer(countLyr)) {
            this.map.removeLayer(countLyr);
          }
          if (this.map.getLayer(countLyrBg)) {
            this.map.removeLayer(countLyrBg);
          }
        } catch (removeError) {
          console.warn(
            `Error removing country layers for ${country.iso}:`,
            removeError
          );
        }
      }
    });
  }

  async loadSources(geoJsonFiles: GeoJsonFile[]) {
    for (const geoJsonFile of geoJsonFiles) {
      queue.add(async () => {
        await this.processSource(geoJsonFile);
      });
    }

    if (!this.props.public) {
      await queue.onIdle().then(() => {
        this.setState({ loading: true });
        this.props.onLoadingStateChange?.(true);
      });
    }

    await queue.start();
  }

  async processSource(geoJsonFile: GeoJsonFile, data?: any, filter?: Filter) {
    if (!this.map || this.map.getSource(geoJsonFile.name)) {
      return;
    }

    if (!data) {
      data = await request(`${baseApiUrl}${geoJsonFile.url}`, {});
    }

    let features;

    if (this.props.public) {
      features = data.features.map((feature: any) => {
        if (feature.geometry.type === 'Point') {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              aggregatedLongitude: feature.geometry.coordinates[0] * (feature.properties.numberPlanted || 1),
              aggregatedLatitude: feature.geometry.coordinates[1] * (feature.properties.numberPlanted || 1),
            }
          };
        }
        return feature;
      });
    } else {
      const allFeatures = [...data.features, ...(this.state.features || [])];

      // Deduplicate features by tree ID to prevent double-counting
      // when multiple geojson sources contain the same trees
      const seenIds = new Set<number>();
      const deduplicatedFeatures = allFeatures.filter((feature: any) => {
        if (!feature || !feature.properties || !feature.properties.id) {
          return true; // keep features without an ID
        }
        if (seenIds.has(feature.properties.id)) {
          return false;
        }
        seenIds.add(feature.properties.id);
        return true;
      });

      let filteredFeatures = deduplicatedFeatures;
      if (filter) {
        filteredFeatures = deduplicatedFeatures.filter((feature: any) => {
          if (!feature || !feature.properties) {
            return false;
          }

          let state = true;

          if (filter.contractId) {
            state = state && feature.properties.contractId === filter.contractId;
          }

          return state;
        });
      }

      features = filteredFeatures.map((feature: any) => {
        if (feature.geometry.type === 'Point') {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              aggregatedLongitude: feature.geometry.coordinates[0] * (feature.properties.numberPlanted || 1),
              aggregatedLatitude: feature.geometry.coordinates[1] * (feature.properties.numberPlanted || 1),
            }
          };
        }
        return feature;
      });
    }

    // Only auto-finish loading if we're not in the middle of a country-specific selection flow
    // (In that case, the country selection logic handles its own loading lifecycle)
    if (!this.selectedCountryId) {
      this.setState({ loading: false });
      this.props.onLoadingStateChange?.(false);
    }

    const aggregatedFeatures = this.aggregatedFeaturesData(features);

    if (!this.state.originalFeatures) {
      this.setState({ originalFeatures: aggregatedFeatures }, () => {
        if (this.props.public) {
          this.categorizeFeaturesByCountry();
        }
      });
    }

    this.setState({ features: aggregatedFeatures });

    this.map.addSource(geoJsonFile.name, {
      type: 'geojson',
      maxzoom: sourceMaxZoom,
      cluster: true,
      clusterMinPoints,
      clusterMaxZoom,
      clusterRadius: 30,
      promoteId: 'id', // Essential for feature-state based selection
      clusterProperties: {
        treeCount: ['+', ['get', 'numberPlanted']],
      },
      data: {
        type: 'FeatureCollection',
        features: aggregatedFeatures
      },
    });

    await this.loadImages(images);
    await this.setLayers(geoJsonFile.name);

    // On the public map this generic source is a data carrier: the clusters are rendered by
    // the dedicated `country-trees-<ISO>` source, which is also the one the loading overlay
    // waits for. Leaving these layers visible drew every cluster twice - once when this source
    // was populated (before the zoom transition ended) and once when the dedicated source was.
    if (this.props.public) {
      [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach((layerType) => {
        const layerId = getLayerId(layerType, geoJsonFile.name);
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, "visibility", "none");
        }
      });
    }

    this.setupOnClusterClick(layers.clusters, geoJsonFile.name);

    this.map.on('mouseenter', geoJsonFile.name, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', geoJsonFile.name, () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.setupEventHandlersForSource(geoJsonFile.name);
  }

  hideTreeLayersForSource(sourceId: string) {
    [layers.clusters, layers.clusterCount, layers.unclusteredPoint].forEach(
      (layerType) => {
        const layerId = getLayerId(layerType, sourceId);
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, "visibility", "none");
        }
      }
    );
  }

  setLayersForSource(sourceId: string) {
    const { removedTrees } = this.state;

    // Cleanup existing layers in exact order to release the source
    [
      layers.selectedUnclusteredPointShadow,
      layers.selectedUnclusteredPoint,
      layers.unclusteredPoint,
      layers.clusterCount,
      layers.clusters
    ].forEach((layerType) => {
      const layerId = getLayerId(layerType, sourceId);
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });

    // 2. Add regular layers
    this.map.addLayer(
      getClustersLayer(layers.clusters, sourceId, removedTrees)
    );

    this.map.addLayer(
      getUnclusteredPointLayer(layers.unclusteredPoint, sourceId, removedTrees)
    );

    this.map.addLayer(
      getClusterCountLayer(layers.clusterCount, sourceId, removedTrees)
    );

    // 3. Add selection layers (visibility controlled via feature-state)
    // Add shadow layer FIRST (at the bottom of selection stack but on top of regular layers)
    this.map.addLayer(
      getSelectionShadowLayer(layers.selectedUnclusteredPoint, sourceId)
    );

    // Add persistent selection icon layer
    this.map.addLayer(
      getSelectedUnclusteredPointLayer(layers.selectedUnclusteredPoint, sourceId)
    );

    // Explicitly force visibility to visible for all added layers
    [
      layers.clusters,
      layers.clusterCount,
      layers.unclusteredPoint,
      layers.selectedUnclusteredPoint,
      layers.selectedUnclusteredPointShadow
    ].forEach((layerType) => {
      const layerId = getLayerId(layerType, sourceId);
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, "visibility", "visible");
      }
    });
  }

  setupMouseEvents(sourceId: string) {
    [layers.clusters, layers.unclusteredPoint].forEach((layerType) => {
      const layerId = getLayerId(layerType, sourceId);

      this.map.off("mouseenter", layerId);
      this.map.off("mouseleave", layerId);

      this.map.on("mouseenter", layerId, () => {
        this.map.getCanvas().style.cursor = "pointer";
      });

      this.map.on("mouseleave", layerId, () => {
        this.map.getCanvas().style.cursor = "";
      });
    });
  }

  onClusterClick(e: any, layerId: string, sourceId: string) {
    const features = this.map.queryRenderedFeatures(e.point, {
      layers: [getLayerId(layerId, sourceId)],
    });

    if (!features || features.length === 0) {
      return;
    }

    const clusterId = features[0].properties.cluster_id;
    const pointCount = Number(features[0].properties.point_count);

    // Display all badge's children centered on the map, or its trees.
    const renderedCenter = features[0].geometry.coordinates;
    const source = this.map.getSource(sourceId);

    if (!source || !source.getClusterChildren) {
      return;
    }

    // What a click on a badge is for: seeing what it is made of. The camera lands as deep as the pieces
    // the clicked one is made of allow it, with all of them on screen inside 70% of the map.
    this.frameClusterFamily(source, clusterId, pointCount, renderedCenter).catch((error) =>
      console.warn(`Could not frame the children of cluster ${clusterId}:`, error)
    );
  }

  /** Ground metres per pixel at a latitude and a zoom; mapbox tiles are 512 px wide. */
  private metresPerPixel(latitude: number, zoom: number): number {
    return 78271.484 * Math.cos((latitude * Math.PI) / 180) / Math.pow(2, zoom);
  }

  /**
   * Grows a box by one clustering radius on every side. The trees a badge stands for can sit up to one
   * radius away from the badge's own position, so a box drawn from cluster centres alone leaves the
   * outermost badges and tree icons of the group outside the frame. The radius is taken at the camera's
   * zoom, which is at or below the zoom the clicked badge splits at - the larger of the two.
   */
  private growBoundsByClusteringRadius(
    bounds: [[number, number], [number, number]]
  ): [[number, number], [number, number]] {
    const latitude = (bounds[0][1] + bounds[1][1]) / 2;
    const metres = clusterRadiusPx * this.metresPerPixel(latitude, this.map.getZoom());
    const latitudeDegrees = metres / 111320;
    const longitudeDegrees = latitudeDegrees / Math.max(0.1, Math.cos((latitude * Math.PI) / 180));

    return [
      [bounds[0][0] - longitudeDegrees, bounds[0][1] - latitudeDegrees],
      [bounds[1][0] + longitudeDegrees, bounds[1][1] + latitudeDegrees],
    ];
  }

  /**
   * Frames a badge on what it is made of: every badge and tree icon that replaces it once clicked, all
   * inside 70% of the map, and as deep as that allows - never below the zoom the badge splits at, where
   * they are not drawn separately yet.
   *
   * Which pieces those are depends on the zoom, so the frame is solved for. The trees are the deepest
   * pieces there are and a badge with a bounded number of them is asked for them directly (exact and one
   * round trip). A heavier badge is walked instead: the pieces drawn at the zoom its children alone would
   * give are collected from the cluster tree, and their own box is framed. Measured on preprod, Uganda's
   * 71,508-point badge: 16 pieces at the children's zoom, 3.4 ms per walk, framed at 8.97 with all of them
   * inside - where the children's positions alone gave 34-57% of the map, their trees reaching a whole
   * clustering radius further out than their centres.
   */
  private async frameClusterFamily(
    source: any,
    clusterId: any,
    pointCount: number,
    renderedCenter: [number, number]
  ): Promise<void> {
    const container = this.map.getContainer();
    const padding = {
      top: clusterFramePadding * container.offsetHeight,
      bottom: clusterFramePadding * container.offsetHeight,
      left: clusterFramePadding * container.offsetWidth,
      right: clusterFramePadding * container.offsetWidth,
    };

    const splitZoom = await this.clusterQuery<number>("getClusterExpansionZoom", source, [clusterId]);
    const children = (await this.clusterQuery<any[]>("getClusterChildren", source, [clusterId])) || [];

    // A badge that does not split holds one group of trees, all of them within one clustering radius of
    // the badge's own position: that radius is the whole frame.
    if (children.length <= 1) {
      this.landOnClusterFrame(this.growBoundsByClusteringRadius([renderedCenter, renderedCenter]), splitZoom, padding);
      return;
    }

    if (pointCount && pointCount <= maxClusterLeaves) {
      const leaves = (await this.clusterQuery<any[]>("getClusterLeaves", source, [clusterId, pointCount, 0])) || [];

      if (leaves.length > 0) {
        this.landOnClusterFrame(this.boundsOfPoints(leaves.map((leaf: any) => leaf.geometry.coordinates)), splitZoom, padding);
        return;
      }
    }

    const childrenBounds = this.boundsOfPoints(children.map((child: any) => child.geometry.coordinates));
    const childrenCamera = this.map.cameraForBounds(childrenBounds, { padding });
    const candidateZoom = childrenCamera && isFinite(childrenCamera.zoom) ? childrenCamera.zoom : clusterMaxZoom + 1;
    const pieces = await this.collectDrawnPieces(source, clusterId, renderedCenter, candidateZoom);

    this.landOnClusterFrame(
      pieces ? this.boundsOfPoints(pieces) : this.growBoundsByClusteringRadius(childrenBounds),
      splitZoom,
      padding
    );
  }

  /**
   * The pieces a cluster is drawn as at a zoom: below its expansion zoom it is one badge, at or above it
   * it is replaced by its children, recursively. Null when the walk runs past `maxClusterWalk` pieces,
   * i.e. when collecting the answer would cost more than the camera move is worth.
   */
  private async collectDrawnPieces(
    source: any,
    clusterId: any,
    clusterCenter: [number, number],
    zoom: number
  ): Promise<[number, number][] | null> {
    const pieces: [number, number][] = [];
    const budget = { left: maxClusterWalk };

    const visit = async (id: any, center: [number, number]): Promise<void> => {
      if (budget.left <= 0) {
        return;
      }

      const expansionZoom = await this.clusterQuery<number>("getClusterExpansionZoom", source, [id]);

      if (expansionZoom === null || expansionZoom > zoom) {
        pieces.push(center);
        return;
      }

      budget.left -= 1;

      const children = (await this.clusterQuery<any[]>("getClusterChildren", source, [id])) || [];

      if (children.length === 0) {
        pieces.push(center);
        return;
      }

      await Promise.all(children.map((child: any) => visit(child.properties.cluster_id, child.geometry.coordinates)));
    };

    await visit(clusterId, clusterCenter);

    return budget.left <= 0 ? null : pieces;
  }

  /** Wraps one of the source's cluster callbacks; resolves to null when it reports an error. */
  private clusterQuery<T>(method: string, source: any, args: any[]): Promise<T | null> {
    return new Promise((resolve) => {
      source[method](...args, (error: any, result: T) => resolve(error ? null : result));
    });
  }

  /** Frames a box inside the padding, never below `minZoom`, never past the zoom badges are drawn at. */
  private landOnClusterFrame(
    bounds: [[number, number], [number, number]],
    minZoom: number | null,
    padding: { top: number; bottom: number; left: number; right: number }
  ) {
    const camera = this.map.cameraForBounds(bounds, { padding });

    // A box with no size - every piece at the same point - has no camera to compute. Such a group is read
    // at the deepest zoom its badges are drawn at, which is the zoom clustering stops at.
    const hasCamera = camera && typeof camera.zoom === "number" && isFinite(camera.zoom);
    const center = hasCamera && camera.center
      ? camera.center
      : [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
    const fitZoom = hasCamera ? camera.zoom : clusterMaxZoom + 1;

    this.map.easeTo({
      center,
      zoom: Math.min(Math.max(fitZoom, minZoom || 0), clusterMaxZoom + 1),
      duration: 800,
      pitch: this.map.getPitch(),
      bearing: this.map.getBearing(),
      essential: true,
    });
  }

  private boundsOfPoints(points: [number, number][]): [[number, number], [number, number]] {
    const longitudes = points.map((point) => point[0]);
    const latitudes = points.map((point) => point[1]);

    return [
      [Math.min(...longitudes), Math.min(...latitudes)],
      [Math.max(...longitudes), Math.max(...latitudes)],
    ];
  }

  setupOnClusterClick(layerId: string, sourceId: string) {
    const fullLayerId = getLayerId(layerId, sourceId);

    this.map.off("click", fullLayerId);

    this.map.on("click", fullLayerId, (e: any) => {
      this.onClusterClick(e, layerId, sourceId);
    });
  }

  setOpenPopupOnClick(layerId: string, sourceId: string) {
    const fullLayerId = getLayerId(layerId, sourceId);

    this.map.off("click", fullLayerId);

    this.map.on("click", fullLayerId, this.openPopup);
  }

  /**
   * Where the tree of the open popup sits on screen, or null when it is not known (a popup opened
   * without its coordinates). The popup hands this to `collapseModalAtPoint()` so that closing it
   * animates towards the icon instead of towards the middle of the window.
   */
  private getPopupMarkerScreenPoint(): { x: number; y: number } | null {
    if (!this.map || !this.popupMarkerCoordinates) {
      return null;
    }

    try {
      // `project()` is relative to the map container, so the canvas offset is added to get the
      // position in the viewport (which is what the modal's transform-origin is measured in).
      const projected = this.map.project(this.popupMarkerCoordinates);
      const canvasRect = this.map.getCanvas().getBoundingClientRect();

      return { x: canvasRect.left + projected.x, y: canvasRect.top + projected.y };
    } catch (error) {
      console.warn('Could not project the popup marker position:', error);
      return null;
    }
  }

  openPopup(e: any, markerCoordinates?: [number, number] | null) {
    let id: any = null;
    let clickedSourceId: string | null = null;
    let markerPoint: [number, number] | null = markerCoordinates || null;

    if (e && e.features && e.features[0]) {
      if (e.features[0].properties) {
        id = e.features[0].properties.id;
      }
      clickedSourceId = e.features[0].source;

      // The clicked feature carries its own position: that is the icon the popup belongs to, and
      // therefore the point the closing animation is directed at.
      const coordinates = e.features[0].geometry?.coordinates;
      if (Array.isArray(coordinates) && coordinates.length >= 2) {
        markerPoint = [coordinates[0], coordinates[1]];
      }
    } else if (typeof e === 'number' || typeof e === 'string') {
      id = e;
    }

    // A single click reaches this method through two registered handlers (the source-level one and
    // the layer-level one from `setOpenPopupOnClick`), so the duplicate has to be dropped *before*
    // any work - the selection update below used to run twice per click.
    if (!id || this.clickProcessing) return;

    // Its deletion has been accepted and its icon is about to go: reopening its popup now would show a tree
    // the server no longer has.
    if (this.treesBeingDeleted[String(id)]) return;

    this.popupMarkerCoordinates = markerPoint;

    this.clickProcessing = true;
    const openToken = ++this.popupOpenToken;

    // 1. Highlight immediately (Surgical update, skip React state for instant feedback)
    this.markAsSelected({ treeId: id, openPopup: false }, { skipState: true, sourceId: clickedSourceId });
    if (this.map) this.map.triggerRepaint?.();

    const { language, template } = this.props;
    const { user } = this.context;

    // 2. Give the map the two frames it needs to draw the selection shadow, so the user sees it
    // before the popup covers the view. This used to be a fixed 120ms waiting for the same thing.
    void (async () => {
      await this.waitForMapRender(2);

      // The popup was closed, or the map was reset, while we waited: abandon this open.
      if (openToken !== this.popupOpenToken) return;

      // 3. Match the React state to the selection block 1 already applied - the second full
      // `markAsSelected()` pass over every source is not needed for that.
      this.setState({ selectedTreeId: id });

      // Handle transitions if another popup is already open
      if (this.isPopupCurrentlyOpen) {
        this.closeAllPopups();
      }

      // 4. Open the new popup if data fetch succeeds, otherwise handle ghost marker error
      TreeRepository.getTree({ params: { treeId: id } })
        .then(() => {
          this.openPopupInternal(id, language, template, user);

          // Reset processing flag with a small buffer
          setTimeout(() => {
            this.clickProcessing = false;
          }, 300);
        })
        .catch((error: any) => {
          // 1. Strict lint-safe extraction from Mapbox click event feature
          const mapFeature = e?.features?.[0];
          const clickedMarkerId = mapFeature?.properties?.id || mapFeature?.properties?.tree_id || mapFeature?.id;

          console.error("API Fetch failed. Detected target marker ID:", clickedMarkerId, error);

          // 2. CRITICAL PROTECTION: Only proceed with deletion if we actually captured the specific clicked marker ID
          if (clickedMarkerId) {
            const targetIdStr = clickedMarkerId.toString();
            console.warn(`[VAPORIZE] Target ghost marker verified: ${targetIdStr}. Wiping from UI context.`);

            // Call the robust removeTree method using the explicit targeted ID
            if (typeof this.removeTree === 'function') {
              this.removeTree(targetIdStr);
            }

            // Clear active map tracking states safely
            this.currentSelectedTreeId = null;
            this.currentPopupTreeId = null;

            // Turn off error modal states cleanly to prevent lint/state race conditions
            this.setState({
              showErrorModal: false,
              selectedTreeId: null
            });
            return;
          }

          // 3. Fallback: If no event-driven ID exists, just close any hanging error UI without deleting anything!
          this.setState({ showErrorModal: false });
        });
    })();
  }

  private openPopupInternal(id: any, language: string, template: any, user: any) {
    this.isPopupCurrentlyOpen = true;
    this.currentPopupTreeId = id;
    const { updateState } = this.context;

    const modalWindowNode = document.createElement("div");

    const root = createRoot(modalWindowNode);
    this.openPopups.set(modalWindowNode, root);
    const Template = template;

    try {
      root.render(
        <MapPopupErrorBoundary fallback={<p>Something went wrong</p>}>
          <Template
            user={user}
            treeId={id}
            contracts={this.context.contracts}
            reloadTrees={this.removeTree}
            // The popup is rendered in a root of its own (see `openPopupInternal`), so the context is
            // not reachable from it: its state updater has to be handed over like every other prop.
            updateState={updateState}
            language={language}
            // Lets the popup collapse into its own tree icon while closing.
            getMarkerScreenPoint={() => this.getPopupMarkerScreenPoint()}
            onCloseStart={() => {
              this.clickProcessing = true;
            }}
            onClose={() => {
              this.closePopupForTree(id, root, modalWindowNode);
            }}
          />
        </MapPopupErrorBoundary>
      );
    } catch (error) {
      console.error("Error rendering popup:", error);
      this.resetPopupState();
    }
  }

  closeAllPopups() {
    // Unmount the React trees: a popup's node is detached from the document, so it cannot be found
    // - nor torn down - through the DOM.
    this.openPopups.forEach((root, node) => {
      try {
        root.unmount();
      } catch (error) {
        console.warn("Error unmounting popup:", error);
      }
      node.remove();
    });
    this.openPopups.clear();

    this.resetPopupState();
  }

  closePopupForTree(treeId: any, root: any, modalWindowNode: HTMLElement) {
    try {
      root.unmount();
      modalWindowNode.remove();
    } catch (error) {
      console.warn("Error removing popup:", error);
    }
    this.openPopups.delete(modalWindowNode);

    this.resetPopupState();
  }

  resetPopupState() {
    // Invalidate any open request that is still waiting for the map frames.
    this.popupOpenToken++;

    this.clickProcessing = true;
    this.isPopupCurrentlyOpen = false;
    this.currentPopupTreeId = null;

    // The deletion that was waiting for this popup to be over: the icon goes now, having been un-clickable
    // for the whole time that window was closing.
    const deferredDeletion = this.deletionAwaitingPopupClose;
    if (deferredDeletion) {
      this.deletionAwaitingPopupClose = null;
      delete this.treesBeingDeleted[String(deferredDeletion.treeId)];
      this.finishTreeRemoval(deferredDeletion.treeId, deferredDeletion.increment);
    }

    setTimeout(() => {
      this.clickProcessing = false;
    }, 300);

    if (this.map) {
      this.map.triggerRepaint?.();
    }
  }

  findLayer(layerId: string) {
    const { geoJsonFiles } = this.props;
    for (const geoJsonFile of geoJsonFiles) {
      const layer = this.map.getLayer(getLayerId(layerId, geoJsonFile.name));
      if (layer) {
        return layer;
      }
    }
  }

  private async loadSvgAsImageData(url: string, width: number = 35, height: number = 50): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get 2D canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        try {
          const imageData = ctx.getImageData(0, 0, width, height);
          resolve(imageData);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => {
        reject(new Error(`Failed to load SVG image at ${url}`));
      };
      img.src = url;
    });
  }

  async loadImages(images: any[]) {
    const notLoadedImages = images.filter((img) => !this.map.hasImage(img.id));
    if (notLoadedImages.length === 0) {
      return;
    }

    await Promise.all(
      images.map(
        async (img) => {
          if (this.map.hasImage(img.id)) return;

          if (img.url.endsWith('.svg')) {
            try {
              const imageData = await this.loadSvgAsImageData(img.url, 35, 50);
              if (!this.map.hasImage(img.id)) {
                this.map.addImage(img.id, imageData);
              }
            } catch (err) {
              console.warn(`Failed to load SVG image ${img.id} via canvas:`, err);
            }
            return;
          }

          return new Promise<void>((resolve) => {
            this.map.loadImage(img.url, (error: any, res: any) => {
              if (error) {
                console.warn(`Could not load map icon ${img.id} at ${img.url}:`, error);
                resolve();
                return;
              }
              if (!this.map.hasImage(img.id)) {
                this.map.addImage(img.id, res);
              }
              resolve();
            });
          })
        }
      )
    );
  }

  async setLayers(sourceId: string) {
    const { removedTrees } = this.state;

    if (this.map.getLayer(getLayerId(layers.clusters, sourceId))) {
      this.map.removeLayer(getLayerId(layers.clusters, sourceId));
    }
    if (this.map.getLayer(getLayerId(layers.clusterCount, sourceId))) {
      this.map.removeLayer(getLayerId(layers.clusterCount, sourceId));
    }
    if (this.map.getLayer(getLayerId(layers.unclusteredPoint, sourceId))) {
      this.map.removeLayer(getLayerId(layers.unclusteredPoint, sourceId));
    }

    this.map.addLayer(
      getClustersLayer(layers.clusters, sourceId, removedTrees)
    );
    this.map.addLayer(
      getUnclusteredPointLayer(layers.unclusteredPoint, sourceId, removedTrees)
    );
    this.map.addLayer(
      getClusterCountLayer(layers.clusterCount, sourceId, removedTrees)
    );

    // Add selection layers
    this.map.addLayer(
      getSelectionShadowLayer(layers.selectedUnclusteredPoint, sourceId)
    );
    this.map.addLayer(
      getSelectedUnclusteredPointLayer(layers.selectedUnclusteredPoint, sourceId)
    );

    // Explicitly force visibility to visible for all added layers
    [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach((layerType) => {
      const layerId = getLayerId(layerType, sourceId);
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, "visibility", "visible");
      }
    });

    const clusterLayerId = getLayerId(layers.clusters, sourceId);
    const clusterCountLayerId = getLayerId(layers.clusterCount, sourceId);
    const unclusteredLayerId = getLayerId(layers.unclusteredPoint, sourceId);

    if (!layerIds.includes(clusterLayerId)) {
      layerIds.push(clusterLayerId);
    }
    if (!layerIds.includes(clusterCountLayerId)) {
      layerIds.push(clusterCountLayerId);
    }
    if (!layerIds.includes(unclusteredLayerId)) {
      layerIds.push(unclusteredLayerId);
    }

    if (!this.clusteredLayers.includes(clusterLayerId)) {
      this.clusteredLayers.push(clusterLayerId);
    }
  }

  cleanupEventHandlers(sourceId: string) {
    console.log(`Cleaning up ALL handlers for source: ${sourceId}`);

    const layerTypes = [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint];

    layerTypes.forEach(layerType => {
      const layerId = getLayerId(layerType, sourceId);

      const popupKey = `popup-${layerId}`;
      const clusterKey = `cluster-${layerId}`;
      const enterKey = `enter-${layerId}`;
      const leaveKey = `leave-${layerId}`;

      const popupHandler = this.getHandlerRef(popupKey);
      const clusterHandler = this.getHandlerRef(clusterKey);
      const enterHandler = this.getHandlerRef(enterKey);
      const leaveHandler = this.getHandlerRef(leaveKey);

      if (popupHandler) {
        this.map.off("click", layerId, popupHandler);
        this.removeHandlerRef(popupKey);
      }

      if (clusterHandler) {
        this.map.off("click", layerId, clusterHandler);
        this.removeHandlerRef(clusterKey);
      }

      if (enterHandler) {
        this.map.off("mouseenter", layerId, enterHandler);
        this.removeHandlerRef(enterKey);
      }

      if (leaveHandler) {
        this.map.off("mouseleave", layerId, leaveHandler);
        this.removeHandlerRef(leaveKey);
      }

      try {
        this.map.off("click", layerId);
        this.map.off("mouseenter", layerId);
        this.map.off("mouseleave", layerId);
      } catch (error) {
        console.warn(`Error during brute force cleanup for ${layerId}:`, error);
      }
    });

    try {
      this.map.off("click", sourceId);
      this.map.off("mouseenter", sourceId);
      this.map.off("mouseleave", sourceId);
    } catch (error) {
      console.warn(`Error cleaning up source-level handlers for ${sourceId}:`, error);
    }
  }

  componentWillUnmount() {
    if (this.orientationHandler && this.orientationEventName) {
      window.removeEventListener(this.orientationEventName, this.orientationHandler);
      this.orientationHandler = null;
      this.orientationEventName = null;
    }

    this.closeAllPopups();

    if (this.map) {
      try {
        this.removeCountryLayers();
      } catch (e) {
        console.warn("Could not remove country layers on unmount:", e);
      }
      try {
        this.map.remove();
      } catch (e) {
        console.warn("Could not remove map instance on unmount:", e);
      }
      this.map = null;
    }

    this.props.setCountrySpecificData && this.props.setCountrySpecificData([]);
  }

  removeCountryLayers() {
    if (!this.map) return;
    try {
      if (this.map.getLayer && this.map.getLayer(layers.countryLabels)) {
        this.map.removeLayer(layers.countryLabels);
      }
      if (this.map.getSource && this.map.getSource("country-boundaries")) {
        this.map.removeSource("country-boundaries");
      }
    } catch (e) {
      console.warn("Could not remove country layers:", e);
    }
  }

  // Called by the tree popup once the server has accepted a deletion. The icon is not taken away here: it
  // stays where it is, un-clickable, while that window closes, and goes once it is over (`resetPopupState`
  // picks it up). A call with no increment - the cleanup of an icon whose tree
  // the API no longer knows - has no window to wait for and goes straight through.
  removeTree(idToRemove: number | string, increment?: number) {
    if (increment !== undefined && increment !== null && this.isPopupCurrentlyOpen) {
      this.treesBeingDeleted[String(idToRemove)] = true;
      this.deletionAwaitingPopupClose = { treeId: idToRemove, increment };
      return;
    }

    this.finishTreeRemoval(idToRemove, increment);
  }

  // Takes the icon out of the map: the layers, the list, the counter and the selection. Reached once the
  // popup that asked for the deletion is over, or at once for a call with nothing to wait for.
  finishTreeRemoval(idToRemove: number | string, increment?: number) {
    const { geoJsonFiles, tableRef } = this.props;
    const { removedTrees } = this.state;
    const { counterRef, contract, updateTrees } = this.context;

    // 1. Reset selection state if the deleted tree was currently selected
    const numericId = Number(idToRemove);
    const stringId = String(idToRemove);

    const filteredFeatures = this.state.features
      ? this.state.features.filter((f: any) => f.properties?.id?.toString() !== numericId.toString() && f.properties?.id?.toString() !== stringId.toString())
      : [];
    const filteredOriginalFeatures = this.state.originalFeatures
      ? this.state.originalFeatures.filter((f: any) => f.properties?.id?.toString() !== numericId.toString() && f.properties?.id?.toString() !== stringId.toString())
      : [];

    if (this.currentSelectedTreeId && (Number(this.currentSelectedTreeId) === numericId || String(this.currentSelectedTreeId) === stringId)) {
      this.currentSelectedTreeId = null;
    }

    // 2. Explicitly remove the 'selected' feature-state for the deleted tree specifically (numeric and string ID) across sources
    const allSources = [
      ...geoJsonFiles.map(f => f.name),
      'country-trees',
      'selected-tree',
      'filter',
      'filter-clustered',
      ...(this.state.selectedCountryId ? [`country-trees-${this.state.selectedCountryId}`] : [])
    ];

    if (this.map) {
      allSources.forEach(sourceId => {
        if (this.map.getSource(sourceId)) {
          try {
            this.map.removeFeatureState({ source: sourceId, id: numericId }, "selected");
          } catch (e) { /* ignore */ }
          try {
            this.map.removeFeatureState({ source: sourceId, id: stringId }, "selected");
          } catch (e) { /* ignore */ }
        }
      });
      this.map.triggerRepaint?.();
    }

    // 3. Clear data from 'selected-tree' source if it was created for this tree
    if (this.map && this.map.getSource('selected-tree')) {
      try {
        (this.map.getSource('selected-tree') as any).setData({
          type: 'FeatureCollection',
          features: []
        });
      } catch (e) { /* ignore */ }
    }

    geoJsonFiles.forEach((geoJsonFile: GeoJsonFile) => {
      const layerId = getLayerId(layers.unclusteredPoint, geoJsonFile.name);
      if (removedTrees && !removedTrees[layerId]) {
        removedTrees[layerId] = [];
      }

      removedTrees[layerId].push(idToRemove);

      this.removeLayers(geoJsonFile.name);
      this.setLayers(geoJsonFile.name);
    });

    if (increment !== undefined && increment !== null) {
      (counterRef as MutableRefObject<TreeCounter>).current?.updateTrees(
        increment
      );

      // Same shape as the sync paths: the delta first, then the contract's counter asked to the server.
      // A deletion the API refuses must not leave the counter short by those trees until the next sync.
      updateTrees?.(increment, contract?.id);
    }
    // Update the map parent state to remove the deleted tree from the table list view instantly. A
    // `React.Ref` can be either form and only the object one carries a `current`.
    const table = tableRef && 'current' in tableRef ? tableRef.current : null;
    if (table && typeof table.props.onDeleteTree === "function") {
      table.props.onDeleteTree(numericId);
    }

    this.setState({
      removedTrees,
      selectedTreeId: null,
      features: filteredFeatures,
      originalFeatures: filteredOriginalFeatures
    }, () => {
      if (this.map) {
        // Clear selection state tracking variables
        this.currentSelectedTreeId = null;
        this.currentPopupTreeId = null;

        // Update the main GeoJSON tree sources and filter sources with the complete filtered list of features
        geoJsonFiles.forEach(geoJsonFile => {
          const source = this.map.getSource(geoJsonFile.name);
          if (source) {
            try {
              (source as any).setData({
                type: 'FeatureCollection',
                features: filteredFeatures
              });
            } catch (e) { /* ignore */ }
          }
        });

        if (this.map.getSource('filter')) {
          try {
            (this.map.getSource('filter') as any).setData({
              type: 'FeatureCollection',
              features: filteredFeatures
            });
          } catch (e) { /* ignore */ }
        }

        if (this.map.getSource('filter-clustered')) {
          try {
            (this.map.getSource('filter-clustered') as any).setData({
              type: 'FeatureCollection',
              features: filteredFeatures
            });
          } catch (e) { /* ignore */ }
        }

        // Forcefully update the filter on the shadow and selected point layers to target nothing
        allSources.forEach(sourceId => {
          const shadowLayerId = getLayerId(layers.selectedUnclusteredPointShadow, sourceId);
          const selectedPointLayerId = getLayerId(layers.selectedUnclusteredPoint, sourceId);
          const filterShadowLayerId = getLayerId("filter-" + layers.selectedUnclusteredPointShadow, sourceId);
          const filterSelectedPointLayerId = getLayerId("filter-" + layers.selectedUnclusteredPoint, sourceId);

          if (this.map.getLayer(shadowLayerId)) {
            try { this.map.setFilter(shadowLayerId, ["==", ["get", "id"], ""]); } catch (e) { /* ignore */ }
          }
          if (this.map.getLayer(selectedPointLayerId)) {
            try { this.map.setFilter(selectedPointLayerId, ["==", ["get", "id"], ""]); } catch (e) { /* ignore */ }
          }
          if (this.map.getLayer(filterShadowLayerId)) {
            try { this.map.setFilter(filterShadowLayerId, ["==", ["get", "id"], ""]); } catch (e) { /* ignore */ }
          }
          if (this.map.getLayer(filterSelectedPointLayerId)) {
            try { this.map.setFilter(filterSelectedPointLayerId, ["==", ["get", "id"], ""]); } catch (e) { /* ignore */ }
          }
        });

        // Force Mapbox canvas to completely repaint and drop cached geometry states
        this.map.triggerRepaint();
      }
    });
  }

  removeLayers(sourceId: string, layerId?: string) {
    for (const layer of Object.values(layers)) {
      if (!layerId) {
        const layerToRemove = getLayerId(layer, sourceId);
        this.map.getLayer(layerToRemove) && this.map.removeLayer(layerToRemove);
      } else {
        this.map.getLayer(layerId) && this.map.removeLayer(layerId);
      }
    }
  }

  markAsSelected(mapPosition: MapPosition, options: { skipState?: boolean; sourceId?: string | null } = {}) {
    if (!this.map) return;

    const { treeId } = mapPosition;
    const { geoJsonFiles } = this.props;
    const { selectedCountryId } = this.state;
    const prevId = this.currentSelectedTreeId;

    // 1. UPDATE INTERNAL STATE
    if (!options.skipState) {
      this.setState({ selectedTreeId: treeId || null });
    }
    this.currentSelectedTreeId = treeId || null;

    // 2. Surgical Selection Switching via Feature State
    // Iterate all potential sources and toggle the 'selected' feature-state.
    const allSources = [
      ...geoJsonFiles.map(f => f.name),
      'country-trees',
      'selected-tree',
      'filter',
      'filter-clustered',
      ...(selectedCountryId ? [`country-trees-${selectedCountryId}`] : [])
    ];

    const numericTreeId = typeof treeId === 'string' ? parseInt(treeId, 10) : treeId;
    const numericPrevId = typeof prevId === 'string' ? parseInt(prevId, 10) : prevId;

    allSources.forEach(sourceId => {
      if (this.map.getSource(sourceId)) {
        // Clear previous highlight
        if (prevId && prevId !== treeId) {
          try {
            this.map.removeFeatureState({ source: sourceId, id: numericPrevId || prevId }, "selected");
          } catch (e) { /* ignore */ }
        }

        // Apply new highlight
        if (treeId) {
          try {
            this.map.setFeatureState({ source: sourceId, id: numericTreeId || treeId }, { selected: true });

            // Ensure the selection layers are on top and reset filters if they were cleared
            const shadowId = getLayerId(layers.selectedUnclusteredPoint + '-shadow', sourceId);
            if (this.map.getLayer(shadowId)) {
              this.map.setFilter(shadowId, ["!", ["has", "point_count"]]);
              this.map.moveLayer(shadowId);
            }
            const layerId = getLayerId(layers.selectedUnclusteredPoint, sourceId);
            if (this.map.getLayer(layerId)) {
              this.map.setFilter(layerId, ["!", ["has", "point_count"]]);
              this.map.moveLayer(layerId);
            }

            // Also restore filter for filter layers if they exist
            const filterShadowId = getLayerId("filter-" + layers.selectedUnclusteredPointShadow, sourceId);
            if (this.map.getLayer(filterShadowId)) {
              this.map.setFilter(filterShadowId, ["!", ["has", "point_count"]]);
              this.map.moveLayer(filterShadowId);
            }
            const filterLayerId = getLayerId("filter-" + layers.selectedUnclusteredPoint, sourceId);
            if (this.map.getLayer(filterLayerId)) {
              this.map.setFilter(filterLayerId, ["!", ["has", "point_count"]]);
              this.map.moveLayer(filterLayerId);
            }
          } catch (e) { /* ignore */ }
        }
      }
    });

    // Trigger a single repaint to ensure visual update
    this.map.triggerRepaint?.();
  }

  changePosition(mapPosition: MapPosition) {
    this.map.setCenter(mapPosition.coordinates);
    this.map.setZoom(
      mapPosition.zoomLevel ? mapPosition.zoomLevel : zoomLevels.max
    );

    // If we are opening a popup, openPopup() handles the highlight
    if (mapPosition.openPopup) {
      this.openPopup(mapPosition.treeId, mapPosition.coordinates as [number, number]);
    } else if (mapPosition.treeId) {
      // Otherwise, highlight manually
      this.markAsSelected({ treeId: mapPosition.treeId, openPopup: false });
    }
  }

  getFeaturesStates() {
    const { featuresToMove } = this.state;
    const featureStates: any[] = [];
    if (!featuresToMove) return featureStates;
    for (const feature of featuresToMove) {
      featureStates.push(
        this.map.getFeatureState({
          source: feature.source,
          id: feature.id,
        })
      );
    }

    return featureStates;
  }

  async updateFeaturesStates(
    features: GeoJSONFeature[],
    contractId: number
  ) {
    for (const feature of features) {
      this.map.setFeatureState(
        {
          source: feature.source,
          id: feature.id,
        },
        {
          properties: {
            ...feature.properties,
            contractId,
          },
        }
      );
    }
  }

  resetFilter(skipFlyTo: boolean = false) {
    // Remove any existing filter layers first
    const sourceNames = ["filter", "filter-clustered", "selected-tree"];
    sourceNames.forEach(sourceId => {
      if (this.map.getSource(sourceId)) {
        this.removeLayers(sourceId);
      }
    });

    const filterLayers = [
      "filter-" + layers.selectedUnclusteredPointShadow,
      "filter-" + layers.selectedUnclusteredPoint,
      "filter-" + layers.unclusteredPoint,
      "filter-" + layers.clusterCount,
      "filter-" + layers.clusters,
    ];

    for (const layerName of filterLayers) {
      for (const sourceName of sourceNames) {
        const layerId = getLayerId(layerName, sourceName);
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      }
    }

    for (const sourceName of sourceNames) {
      if (this.map.getSource(sourceName)) {
        this.map.removeSource(sourceName);
      }
    }

    // Show original layers
    const layerIds = [layers.clusters, layers.clusterCount, layers.unclusteredPoint];
    for (const layerId of layerIds) {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, "visibility", "visible");
      }
    }

    if (this.props.public) {
      if (this.map.getLayer(layers.countryLabels)) {
        this.map.setLayoutProperty(
          layers.countryLabels,
          "visibility",
          "visible"
        );
      }
    }

    // Reset map to initial position and zoom level
    const { initialPosition } = this.props;
    if (!skipFlyTo) {
      this.map.flyTo({
        center: initialPosition.coordinates,
        zoom: initialPosition.zoomLevel || 2,
        duration: 1000,
        pitch: this.map.getPitch(),
        bearing: this.map.getBearing(),
        essential: true
      });
    }

    this.map.off(
      "click",
      getLayerId("filter-" + layers.unclusteredPoint, "filter"),
      this.openPopup
    );
    this.map.off(
      "click",
      getLayerId("filter-" + layers.clusters, "filter"),
      (e: any) => {
        this.onClusterClick(e, "filter-" + layers.clusters, "filter");
      }
    );
  }

  // Calculate distance between two geographic points using Haversine formula
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Find tree feature by ID from all features
  private findTreeFeatureById(treeId: number | string): any {
    const allFeatures = [...(this.state.features || []), ...(this.state.originalFeatures || [])];
    return allFeatures.find(feature =>
      feature.properties?.id?.toString() === treeId.toString()
    );
  }

  // Calculate bounds for all filtered trees to show them all
  private calculateBoundsForFeatures(features: any[]): mapboxgl.LngLatBounds | null {
    if (!features || features.length === 0) {
      return null;
    }

    const bounds = new mapboxgl.LngLatBounds();
    let hasValidCoordinates = false;

    features.forEach(feature => {
      if (feature.geometry.type === 'Point' && feature.geometry.coordinates) {
        const [lng, lat] = feature.geometry.coordinates;
        if (lng && lat && !isNaN(lng) && !isNaN(lat)) {
          bounds.extend([lng, lat]);
          hasValidCoordinates = true;
        }
      }
    });

    return hasValidCoordinates ? bounds : null;
  }

  async applyFilter(filter: any) {
    const features = this.state.features;
    this.currentFilter = filter;

    if (!features || features?.length === 0) {
      return;
    }

    this.setState({ loading: true });
    this.props.onLoadingStateChange?.(true);

    // Check if any filter is actually applied - be very explicit to prevent "Earth view bounce"
    const hasActiveFilters = !!(
      (filter.treeId !== null && filter.treeId !== undefined && String(filter.treeId).trim() !== '') ||
      (filter.contractId) ||
      (filter.validatorId) ||
      filter.dateStart ||
      filter.dateEnd
    );

    // CLEANUP PHASE: Always skip flyTo in resetFilter when called from applyFilter.
    // We will handle ALL animations (Zoom in to tree, Zoom to bounds, or Reset to home) 
    // at the very end of this function for a single, smooth transition.
    this.resetFilter(true);

    // If we're not filtering by a single tree anymore, clear the previous tree ID 
    if (!filter.treeId) {
      this.previousFilteredTreeId = null;
    }

    // Helper function to normalize date to start/end of day
    const normalizeDate = (date: Date | string | number, isEndOfDay: boolean = false): number => {
      // Handle Unix timestamps in seconds (convert to milliseconds)
      let dateValue: string | number | Date = date;
      if (typeof date === 'number' && date < 1e12) {
        dateValue = date * 1000;
      }
      const d = new Date(dateValue);
      if (isEndOfDay) {
        d.setHours(23, 59, 59, 999);
      } else {
        d.setHours(0, 0, 0, 0);
      }
      return d.getTime();
    };

    // Helper function to get feature date as timestamp
    const getFeatureTimestamp = (feature: any): number | null => {
      const dateField = feature.properties.plantingDate ||
        feature.properties.createdAt ||
        feature.properties.plantedAt ||
        feature.properties.date;

      if (!dateField) return null;

      // Handle timestamp in seconds (convert to milliseconds)
      if (typeof dateField === 'number') {
        return dateField > 1e12 ? dateField : dateField * 1000;
      }

      // Handle date strings
      return new Date(dateField).getTime();
    };

    const filteredFeatures: GeoJSONFeature[] = features.filter(
      (feature: GeoJSONFeature) => {
        if (!feature || !feature.properties) {
          return false;
        }

        let state = true;

        // Contract filter
        if (filter.contractId) {
          state = state && feature.properties.contractId === filter.contractId;
          if (!state) return false;
        }

        // Tree ID filter (specific tree)
        if (filter.treeId) {
          state = state && feature.properties.id.toString() === filter.treeId.toString();
          if (!state) return false;
        }

        // Validator filter
        if (filter.validatorId) {
          state = state && feature.properties.validatorId === filter.validatorId;
          if (!state) return false;
        }

        // Date range filter - use flat dateStart and dateEnd properties
        const startDate = filter.dateStart || null;
        const endDate = filter.dateEnd || null;

        // Apply date filter if we have valid dates
        if (startDate && endDate) {
          const featureTimestamp = getFeatureTimestamp(feature);

          if (!featureTimestamp) {
            console.warn(`Tree ${feature.properties.id} has no valid date field`);
            return false;
          }

          const startTimestamp = normalizeDate(startDate, false);
          const endOfDayTimestamp = normalizeDate(endDate, true); // Get end of end date

          state = state && featureTimestamp >= startTimestamp && featureTimestamp <= endOfDayTimestamp;
        }

        return state;
      }
    );

    // Log filtering results
    console.log({
      totalFeatures: features.length,
      filteredCount: filteredFeatures.length,
      filter: {
        contractId: filter.contractId,
        treeId: filter.treeId,
        dateRange: filter.dateStart || filter.dateEnd ? { start: filter.dateStart, end: filter.dateEnd } : null
      }
    });

    if (filteredFeatures.length === 0) {
      console.warn('No features matched the filter criteria');
      // Still show empty map instead of showing all trees
    }

    // Continue with the rest of your existing code...
    if (this.mapBoundingBox) {
      this.mapBoundingBox.setData(filteredFeatures);
      this.mapBoundingBox.setSourcesAndLayers();
    }

    // Hide original layers
    for (const layerId of [...layerIds, ...this.clusteredLayers]) {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, "visibility", "none");
      }
    }

    if (this.props.public) {
      if (this.map.getLayer(layers.countryLabels)) {
        this.map.setLayoutProperty(layers.countryLabels, "visibility", "none");
      }
    }

    try {
      // Clean up existing filter sources
      if (this.map.getSource("filter-clustered")) {
        this.cleanupEventHandlers("filter-clustered");
        [
          layers.selectedUnclusteredPointShadow,
          layers.selectedUnclusteredPoint,
          layers.unclusteredPoint,
          layers.clusterCount,
          layers.clusters,
        ].forEach((layerType) => {
          const layerId = getLayerId("filter-" + layerType, "filter-clustered");
          if (this.map.getLayer(layerId)) {
            this.map.removeLayer(layerId);
          }
        });
        this.map.removeSource("filter-clustered");
      }

      if (this.map.getSource("filter")) {
        this.cleanupEventHandlers("filter");
        const filterLayerId = getLayerId("filter-" + layers.unclusteredPoint, "filter");
        if (this.map.getLayer(filterLayerId)) {
          this.map.removeLayer(filterLayerId);
        }
        this.map.removeSource("filter");
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      // Add the filtered source and wait for it to be ready
      this.map.addSource("filter-clustered", {
        type: "geojson",
        maxzoom: sourceMaxZoom,
        cluster: true,
        clusterMinPoints,
        clusterMaxZoom,
        clusterRadius: 30,
        promoteId: 'id', // Essential for feature-state based selection
        clusterProperties: {
          treeCount: ["+", ["get", "numberPlanted"]],
        },
        data: { type: "FeatureCollection", features: filteredFeatures },
      });

      // Wait for source to be fully loaded
      await new Promise<void>((resolve) => {
        const checkSource = () => {
          if (this.map.getSource("filter-clustered") && this.map.isSourceLoaded("filter-clustered")) {
            resolve();
          } else {
            setTimeout(checkSource, 50);
          }
        };
        checkSource();
      });

      // Now add all layers synchronously
      try {
        // Add cluster layer
        const clusterLayer = getClustersLayer("filter-" + layers.clusters, "filter-clustered");
        if (!this.map.getLayer(getLayerId("filter-" + layers.clusters, "filter-clustered"))) {
          this.map.addLayer(clusterLayer);
        }

        // Add cluster count layer
        const clusterCountLayer = getClusterCountLayer("filter-" + layers.clusterCount, "filter-clustered");
        if (!this.map.getLayer(getLayerId("filter-" + layers.clusterCount, "filter-clustered"))) {
          this.map.addLayer(clusterCountLayer);
        }

        // Add unclustered points layer
        const unclusteredLayer = getUnclusteredPointLayer("filter-" + layers.unclusteredPoint, "filter-clustered");
        if (!this.map.getLayer(getLayerId("filter-" + layers.unclusteredPoint, "filter-clustered"))) {
          this.map.addLayer(unclusteredLayer);
        }

        // Add persistent selection layer (visibility controlled via feature-state)
        if (!this.map.getLayer(getLayerId("filter-" + layers.selectedUnclusteredPoint + "-shadow", "filter-clustered"))) {
          this.map.addLayer(getSelectionShadowLayer("filter-" + layers.selectedUnclusteredPoint, "filter-clustered"));
        }
        if (!this.map.getLayer(getLayerId("filter-" + layers.selectedUnclusteredPoint, "filter-clustered"))) {
          this.map.addLayer(getSelectedUnclusteredPointLayer("filter-" + layers.selectedUnclusteredPoint, "filter-clustered"));
        }

        // Wait a moment for layers to be fully initialized
        await new Promise(resolve => setTimeout(resolve, 300));

        // Verify layers exist before setting up handlers
        const expectedLayers = [
          getLayerId("filter-" + layers.clusters, "filter-clustered"),
          getLayerId("filter-" + layers.clusterCount, "filter-clustered"),
          getLayerId("filter-" + layers.unclusteredPoint, "filter-clustered")
        ];

        const missingLayers = expectedLayers.filter(layerId => !this.map.getLayer(layerId));
        if (missingLayers.length > 0) {
          console.warn('Some filter layers are missing before handler setup:', missingLayers);
          // Wait a bit more and try once more
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Setup event handlers
        this.setupOnClusterClick("filter-" + layers.clusters, "filter-clustered");
        this.setupEventHandlersForSource("filter-clustered");

        // Add click handler for unclustered points
        this.setOpenPopupOnClick("filter-" + layers.unclusteredPoint, "filter-clustered");

        console.log('Filter layers added successfully');

      } catch (error) {
        console.error('Error adding filter layers:', error);
      }

      // Handle specific tree selection with distance-based zoom
      if (filter.treeId && filteredFeatures.length === 1) {
        const treeId = filteredFeatures[0].properties?.id;
        if (treeId) {
          const treeFeature = filteredFeatures[0];
          if (treeFeature.geometry.type === 'Point') {
            const [lng, lat] = treeFeature.geometry.coordinates;

            // First, clear any existing selection
            this.markAsSelected({ treeId: undefined, openPopup: false });

            // Get the source ID for the tree
            const sourceId = 'filter-clustered';

            // Add the selected tree layer directly
            try {
              const layerId = getLayerId(layers.selectedUnclusteredPoint, sourceId);

              // Remove any existing layer first
              if (this.map.getLayer(layerId)) {
                this.map.removeLayer(layerId);
              }

              // Create a new source for the selected tree
              if (!this.map.getSource('selected-tree')) {
                this.map.addSource('selected-tree', {
                  type: 'geojson',
                  promoteId: 'id', // Essential for feature-state based selection
                  data: {
                    type: 'FeatureCollection',
                    features: [treeFeature]
                  }
                });
              } else {
                (this.map.getSource('selected-tree') as any).setData({
                  type: 'FeatureCollection',
                  features: [treeFeature]
                });
              }

              // Add the selection layers
              const shadowLayer = getSelectionShadowLayer(
                layers.selectedUnclusteredPoint,
                'selected-tree'
              );
              const selectedLayer = getSelectedUnclusteredPointLayer(
                layers.selectedUnclusteredPoint,
                'selected-tree'
              );

              this.map.addLayer(shadowLayer);
              this.map.addLayer(selectedLayer);
              this.setOpenPopupOnClick(layers.selectedUnclusteredPoint, 'selected-tree');

              // Update the current selected tree ID
              this.currentSelectedTreeId = treeId;

              if (filter.zoomOut) {
                // Zoom directly to the selected tree
                await new Promise<void>((resolve) => {
                  this.map.flyTo({
                    center: [lng, lat],
                    zoom: 18,
                    pitch: Math.min(this.map.getPitch(), 45),
                    bearing: 0,
                    essential: true
                  });

                  this.map.once('moveend', () => {
                    this.openPopup(treeId, [lng, lat]);
                    resolve();
                  });
                });
              } else {
                this.openPopup(treeId, [lng, lat]);
              }

              // Update previous tree ID for the next filter action
              this.previousFilteredTreeId = treeId;

            } catch (error) {
              console.error('Error highlighting selected tree:', error);
            }
          }
        }
      }

      // Setup mouse handlers
      const clusterLayerId = getLayerId("filter-" + layers.clusters, "filter-clustered");
      const unclusteredLayerId = getLayerId("filter-" + layers.unclusteredPoint, "filter-clustered");

      const enterHandler = () => {
        this.map.getCanvas().style.cursor = "pointer";
      };
      const leaveHandler = () => {
        this.map.getCanvas().style.cursor = "";
      };

      setTimeout(() => {
        if (this.map.getLayer(clusterLayerId) && this.map.getLayer(unclusteredLayerId)) {
          [clusterLayerId, unclusteredLayerId].forEach(layerId => {
            try {
              this.storeHandlerRef(`enter-${layerId}`, enterHandler);
              this.storeHandlerRef(`leave-${layerId}`, leaveHandler);
              this.map.on("mouseenter", layerId, enterHandler);
              this.map.on("mouseleave", layerId, leaveHandler);
            } catch (error) {
              console.warn(`Failed to set up mouse handlers for layer ${layerId}:`, error);
            }
          });
        }
      }, 100);

      // Apply zoom out logic if enabled and not a specific tree selection
      // When zoomOut is false, maintain current map view (don't zoom out)
      if (filter.zoomOut && !filter.treeId) {
        if (hasActiveFilters) {
          // Calculate bounds to show all filtered trees
          const bounds = this.calculateBoundsForFeatures(filteredFeatures);

          if (bounds) {
            // Determine if mobile for padding
            const mapContainer = this.map.getContainer();
            const containerWidth = mapContainer.offsetWidth;
            const isMobile = containerWidth < 768;

            const padding = isMobile
              ? { top: 120, bottom: 80, left: 50, right: 50 }
              : { top: 100, bottom: 70, left: 60, right: 60 };

            this.map.fitBounds(bounds, {
              padding,
              maxZoom: 12, // Reasonable max zoom to see multiple trees
              duration: 1000,
              pitch: this.map.getPitch(),
              bearing: this.map.getBearing(),
              essential: true
            });
          }
        } else {
          // RESET CASE: No active filters, fly back to initial position
          const { initialPosition } = this.props;
          this.map.flyTo({
            center: initialPosition.coordinates,
            zoom: initialPosition.zoomLevel || 2,
            duration: 1000,
            pitch: this.map.getPitch(),
            bearing: this.map.getBearing(),
            essential: true
          });
        }
      }
      // When zoomOut is false and no specific tree, do nothing to maintain current view

      // Only turn off loading if we aren't in a country selection flow
      if (!this.selectedCountryId) {
        this.setState({ loading: false });
        this.props.onLoadingStateChange?.(false);
      }
    } catch (error) {
      console.error('Error applying filter:', error);
      if (!this.selectedCountryId) {
        this.setState({ loading: false });
        this.props.onLoadingStateChange?.(false);
      }
    }
  }

  async resetToInitialCondition() {
    // 1. Aggressively clear all pending timeouts to prevent stale transitions
    if (this.countryTransitionTimeout) {
      clearTimeout(this.countryTransitionTimeout);
      this.countryTransitionTimeout = null;
    }

    // Stop map immediately
    if (this.map && this.map.stop) {
      this.map.stop();
    }

    // Clear any selection highlight exhaustively
    this.markAsSelected({ treeId: undefined, openPopup: false });

    // Reset country highlight filter to prevent stale borders during transition
    if (this.map && this.map.getLayer(layers.countriesHighlight)) {
      try {
        this.map.setFilter(layers.countriesHighlight, ["==", "iso_3166_1_alpha_3", ""]);
      } catch (e) {
        console.warn("Error resetting country highlight filter:", e);
      }
    }

    // Reset all state variables
    this.isPopupCurrentlyOpen = false;
    this.currentSelectedTreeId = null;
    this.currentPopupTreeId = null;
    this.selectedCountryId = null;
    this.clickProcessing = false;
    this.isTransitioningToNewPopup = false;

    // Reset all popup-related state
    this.closeAllPopups();

    // RESET SELECTION STATE BEFORE HIDING LAYERS
    this.selectedCountryId = null;
    if (typeof (this as any).lastRenderedCountryId !== 'undefined') {
      (this as any).lastRenderedCountryId = null;
    }

    // Clear any selected tree markers from ALL sources
    const { geoJsonFiles } = this.props;

    // Remove selected layers from original geoJsonFiles
    geoJsonFiles.forEach((file: GeoJsonFile) => {
      const layerId = getLayerId(layers.selectedUnclusteredPoint, file.name);
      if (this.map.getLayer(layerId)) {
        try {
          this.map.off("click", layerId, this.openPopup);
          this.map.removeLayer(layerId);
        } catch (error) {
          console.warn(`Error removing selected layer ${layerId}:`, error);
        }
      }
    });

    // 1. Remove ALL layers for the generic 'country-trees' source in EXACT order
    const genericSourceId = 'country-trees';
    [
      layers.selectedUnclusteredPointShadow,
      layers.selectedUnclusteredPoint,
      layers.unclusteredPoint,
      layers.clusterCount,
      layers.clusters
    ].forEach((layerType) => {
      const layerId = getLayerId(layerType, genericSourceId);
      if (this.map.getLayer(layerId)) {
        try {
          this.map.off("click", layerId, this.openPopup);
          this.map.removeLayer(layerId);
        } catch (error) {
          console.warn(`Error removing generic layer ${layerId}:`, error);
        }
      }
    });

    // 2. Remove ALL layers for every highlighted country source in EXACT order
    this.props.highlightedCountries?.forEach((country) => {
      const countrySourceId = `country-trees-${country.country_id}`;
      [
        layers.selectedUnclusteredPointShadow,
        layers.selectedUnclusteredPoint,
        layers.unclusteredPoint,
        layers.clusterCount,
        layers.clusters
      ].forEach((layerType) => {
        const layerId = getLayerId(layerType, countrySourceId);
        if (this.map.getLayer(layerId)) {
          try {
            this.map.off("click", layerId, this.openPopup);
            this.map.removeLayer(layerId);
          } catch (error) {
            console.warn(`Error removing country-specific layer ${layerId}:`, error);
          }
        }
      });
    });

    this.hideAllTreeLayers();

    this.props.highlightedCountries?.forEach((country) => {
      const sourceId = `country-trees-${country.country_id}`;

      [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach((layerType) => {
        const layerId = getLayerId(layerType, sourceId);
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, "visibility", "none");
        }
      });
    });

    const genericCountrySourceId = "country-trees";
    [layers.clusters, layers.clusterCount, layers.unclusteredPoint, layers.selectedUnclusteredPoint].forEach((layerType) => {
      const lyrId = getLayerId(layerType, genericCountrySourceId);
      if (this.map.getLayer(lyrId)) {
        this.map.setLayoutProperty(lyrId, "visibility", "none");
      }
    });


    this.setState({
      selectedCountryId: null,
      selectedTreeId: null,
      treesVisible: false
    });

    this.showAllTrees();

    // Re-setup event handlers for original geoJsonFile sources
    setTimeout(() => {
      this.props.geoJsonFiles.forEach((geoJsonFile) => {
        const sourceId = geoJsonFile.name;
        if (this.map.getSource(sourceId)) {
          this.setupEventHandlersForSource(sourceId);
        }
      });
    }, 300);

    if (this.props.onCountryDeselect) {
      this.props.onCountryDeselect();
    }
  }

  async handleBackButtonClick() {
    // Stop the download of the country being left before anything else
    this.props.onCountryDeselect?.();

    // Set loading state immediately - this blocks all interactions and shows the spinner
    // with map in 2D view by default
    // with map in 2D view by default
    this.setState({ loading: true, is3D: false });
    this.props.onLoadingStateChange?.(true);

    // Stop all map animations immediately to prevent conflicts
    if (this.map && this.map.stop) {
      this.map.stop();
    }

    try {
      // Disable all map interactions while loading
      if (this.map) {
        this.map.dragPan.disable();
        this.map.scrollZoom.disable();
        this.map.doubleClickZoom.disable();
        this.map.touchZoomRotate.disable();
      }

      // Reset popup states immediately
      this.isPopupCurrentlyOpen = false;
      this.clickProcessing = false;
      this.closeAllPopups();

      // Clear country-specific data
      this.props.setCountrySpecificData?.([]);

      // Wait for comprehensive cleanup to complete
      await this.resetToInitialCondition();

      // The map is put back in 2D here
      if (this.map) {
        try {
          this.map.setTerrain(null);
          this.map.setFog(null);
          this.map.setPitch(0);
          this.map.setBearing(0);
        } catch (error) {
          console.warn('Could not put the map back in 2D while going back:', error);
        }
      }

      // Reset the map view to the default position. Deliberately not awaited here: the overlay
      // follows the display of the country counts (below), not the camera, so the return flight
      // finishes under it - the same rule as when a country is opened.
      let cameraReset: Promise<void> = Promise.resolve();

      if (this.map && this.props.initialPosition.coordinates) {
        // First, stop any ongoing animations
        if (this.map.stop) {
          this.map.stop();
        }
        // Reset the map view with a smooth transition
        cameraReset = new Promise<void>((resolve) => {
          let finished = false;

          const finishReset = () => {
            if (finished) return;
            finished = true;
            this.cancelWorldViewReset = null;

            if (timeoutId) clearTimeout(timeoutId);

            if (this.map) {
              this.map.off('moveend', onMoveEnd);
              // Ensure final position is exactly as specified
              this.map.jumpTo({
                center: this.props.initialPosition.coordinates,
                zoom: this.props.initialPosition.zoomLevel || zoomLevels.med,
                pitch: 0,
                bearing: 0
              });

              // Re-enable map interactions
              this.map.dragPan.enable();
              this.map.scrollZoom.enable();
              this.map.doubleClickZoom.enable();
              this.map.touchZoomRotate.enable();
            }
            resolve();
          };

          const onMoveEnd = () => {
            finishReset();
          };

          this.map.on('moveend', onMoveEnd);

          // Fallback if moveend never fires or takes too long
          const timeoutId = setTimeout(finishReset, 2500);

          // Lets a country opened during this flight take the camera over (see `selectCountryDirectly`): that
          // fallback would otherwise land the world view on top of the country's zoom. The promise is resolved
          // here, so the Back flow awaiting is not left hanging
          this.cancelWorldViewReset = () => {
            finished = true;
            this.cancelWorldViewReset = null;
            clearTimeout(timeoutId);
            this.map?.off('moveend', onMoveEnd);
            resolve();
          };

          // Start the fly-to animation
          this.map.flyTo({
            center: this.props.initialPosition.coordinates,
            zoom: this.props.initialPosition.zoomLevel || zoomLevels.med,
            pitch: 0,
            bearing: 0,
            duration: 1000,
            essential: true
          });
        });
      }

      // Re-highlight the countries and rebuild their counts while the camera flies back above.
      // This chain used to start only after the flight, while the same refresh - started by the
      // "country data cleared" branch in `componentDidUpdate` - had already put the counts on
      // screen: that gap is what made the overlay outlive them by up to a second. No longer
      // waiting for the camera, and no duplicate chain in `componentDidUpdate` any more.
      await this.refreshWorldViewCounts();

      // Re-enable map interactions
      if (this.map) {
        this.map.dragPan.enable();
        this.map.scrollZoom.enable();
        this.map.doubleClickZoom.enable();
        this.map.touchZoomRotate.enable();
      }

      // Now turn off loading ONLY if we haven't started selecting a new country
      if (!this.selectedCountryId) {
        this.setState({ loading: false });
        this.props.onLoadingStateChange?.(false);
      }

      // The overlay is already gone; the return flight may still be running, and this transition
      // is only over once it lands.
      await cameraReset;
    } catch (error) {
      console.error('Error during back button click:', error);
      if (!this.selectedCountryId) {
        this.setState({ loading: false });
        this.props.onLoadingStateChange?.(false);
      }
    }
  }

  render() {
    const { loading, mapStyle, featuresToMove, selectedCountryId } = this.state;

    return (
      <div>
        <div style={{ height: "85.5vh", width: "100%", position: "relative" }}>
          {loading && <MapDataLoader />}

          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: "9",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MapStyleSwitcher
              mapObject={this.map}
              accessToken={this.accessToken}
              mapStyle={mapStyle}
              onSwitchStyle={async () => this.loadImages(images)}
              public={this.props.public}
            />
            <div
              ref={this.scaleContainerRef}
              style={{
                marginLeft: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* Scale bar, redrawn by `updateScale()` on every map move. Same look as mapbox's
                  own control (`.mapboxgl-ctrl-scale`), whose label it replaces. */}
              <div
                ref={this.scaleLabelRef}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.75)",
                  border: "2px solid #333",
                  borderTop: "none",
                  boxSizing: "border-box",
                  color: "#333",
                  // Same text as the style buttons next to it: `inherit` picks up whatever they
                  // inherit (antd's base font), and the size matches antd's base font size. The box
                  // is twice as tall as it was.
                  fontFamily: "inherit",
                  fontSize: "12px",
                  height: "21px",
                  lineHeight: "20px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              />
            </div>
          </div>

          {/* Don't display the 2D/3D toggle button when map is loading. 
          Afterwards the button is displayed */}
          {!loading && (
            <DimensionToggle
              map={this.map}
              is3D={this.state.is3D}
              onToggle={(is3D) => this.setState({ is3D })}
            />
          )}

          {this.props.public && selectedCountryId && (
            <div
              style={{
                position: "absolute",
                top: "50px",
                // Just above the other map overlays (9 for the style switcher, 10 for
                // DimensionToggle) and below the mobile menu (z-index 99 in
                // sitemenu.less): at 1000 the button stayed on top of the opened menu.
                zIndex: "11",
              }}
            >
              <button
                onClick={() => {
                  this.handleBackButtonClick()
                }}
                style={{
                  backgroundColor: "#007cbf",
                  color: "white",
                  border: "2px solid #ffffff",
                  borderRadius: "6px",
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                  minWidth: "80px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#005a8b";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 12px rgba(0,0,0,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#007cbf";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
                }}
              >
                {/* Use an SVG icon to display the "←" character inside BackButton, cause Android's default font has
                    no glyph for U+2190 and wrongly draws a box instead. */}
                <ArrowLeftOutlined style={{ fontSize: "16px" }} />
                Back
              </button>
            </div>
          )}

          <div id="map" style={{ height: "100%" }}></div>

          {featuresToMove && (
            <TreeMapErrorBoundary>
              <MoveTreesPopup
                open={true}
                features={featuresToMove}
                featureStates={this.getFeaturesStates()}
                onClose={() => {
                  this.setState({ featuresToMove: undefined });
                }}
                onFinish={(
                  features: GeoJSONFeature[],
                  contractId: number
                ) => {
                  const { originalFeatures } = this.state;
                  this.updateFeaturesStates(features, contractId);
                  const updatedFeatures = originalFeatures?.map((current) => {
                    if (
                      _.find(features, (o) => o?.properties?.id === current.id)
                    ) {
                      current = {
                        ...current,
                        properties: { ...current.properties, contractId },
                      };
                    }
                    return current;
                  });
                  this.setState({
                    features: updatedFeatures,
                    originalFeatures: updatedFeatures
                  }, () => {
                    if (this.currentFilter) {
                      this.applyFilter(this.currentFilter);
                    }
                  });
                }}
              />
            </TreeMapErrorBoundary>
          )}
        </div>
      </div>
    );
  }
}